import AskSmartPreplanDrawer from './AskSmartPreplanDrawer';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import NavigationOrderEditor from './NavigationOrderEditor';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Tooltip } from './ui/Tooltip';
import GridLabel from './GridLabel';
import CommunicationProfileLink from './communications/CommunicationProfileLink';
import SyncStatusFooter from './SyncStatusFooter';
import ContainerIcon from './icons/ContainerIcon';
import DashboardPanelIcon from './icons/DashboardPanelIcon';
import PlannerIcon from './icons/PlannerIcon';
import ApiIcon from './icons/ApiIcon';
import BugReportIcon from './icons/BugReportIcon';
import SettingsIcon from './icons/SettingsIcon';
import FeaturesIcon from './icons/FeaturesIcon';
import LifeRingIcon from './icons/LifeRingIcon';
import ReceiptIcon from './icons/ReceiptIcon';
import BugReportForm from './BugReportForm';
import FeatureRequestForm from './FeatureRequestForm';
import HelpdeskTicketForm from './HelpdeskTicketForm';
import ScreenshotEditor from './ScreenshotEditor';
import GlobalSearch from './GlobalSearch';
import DriverRateCalculator from './DriverRateCalculator';
import MarketConditionsPill from './MarketConditionsPill';
import MarketConditionsDrawer from '../pages/scheduling/components/MarketConditionsDrawer';
import { useMarketConditionsPush } from '../hooks/useMarketConditionsPush';
import { usePermissions } from '../contexts/PermissionContext';
import { config } from '../config/config';
import { usePermissionsMode } from '../contexts/PermissionsModeContext';
import UserPermissionsIcon from './icons/UserPermissionsIcon';
import UserTimeIcon from './icons/UserTimeIcon';
import PermissionsModeToolbar from './PermissionsModeToolbar';
import { useIconLegend } from '../contexts/IconLegendContext';
import { useDispatchFooter } from '../contexts/DispatchFooterContext';
import permissionsService from '../services/permissions.service';
import {
  useDisplayPreferences,
  DEFAULT_GROUP_ROW_COLOR,
  DEFAULT_LOCKED_TEXT_COLOR_LIGHT,
  DEFAULT_LOCKED_TEXT_COLOR_DARK,
  LOCKED_TEXT_BASELINE_LIGHTNESS,
  DEFAULT_FIELD_TINT_LIGHT,
  DEFAULT_FIELD_TINT_DARK,
  FIELD_TINT_BASELINE_LIGHT,
  FIELD_TINT_BASELINE_DARK,
  FIELD_TINT_SATURATION_SCALE,
} from '../contexts/DisplayPreferencesContext';
import useNavFullSlide from '../hooks/useNavFullSlide';
import PinIcon from './icons/PinIcon';
import { useNotificationPreferences } from '../contexts/NotificationPreferencesContext';
import ZebraColorPicker from './ZebraColorPicker';
import FontPreferencesSection from './FontPreferencesSection';
import TextCasePreference from './TextCasePreference';
import PrintPreferencesSection from './PrintPreferencesSection';
import { useTheme } from '../contexts/ThemeContext';
import ThemeColorPicker from './ThemeColorPicker';
import IconLegendModal from './IconLegendModal';
import Toggle from './Toggle';
import ImpersonationBanner from './impersonation/ImpersonationBanner';
import UserImpersonationModal from './impersonation/UserImpersonationModal';
import UserHistoryModal from './UserHistoryModal';
import { activityTracker } from '../services/activity.service';
import ClosedTicketToast from './ClosedTicketToast';
import GWI360Assistant from './GWI360Assistant';
import { useWorkingStatusPreference } from '../contexts/WorkingStatusPreferenceContext';
import EnvironmentBanner from './EnvironmentBanner';
import { useProductionReadonlyMode } from '../contexts/ProductionReadonlyModeContext';
import DevelopmentIcon from './icons/DevelopmentIcon';
import DataManagementIcon from './icons/DataManagementIcon';
import { DATA_TABLES, isTableReady, findTableByRoute } from '../config/dataTables.registry';
import AnalyticsIcon from './icons/AnalyticsIcon';
import AircallIcon from './icons/AircallIcon';
import { favoriteTarget } from '../utils/favoriteTarget';
import { FavoriteTabContext, FAVORITE_TAB_PARAM } from '../hooks/useFavoriteTab';
import { useFavorites } from '../hooks/useFavorites';
import { useNavigationOrder, applyNavOrder } from '../hooks/useNavigationOrder';
import { findNavItemByHref } from '../utils/findNavItemByHref';
import { resolveFavoritable } from '../utils/resolveFavoritable';
import { usePageTitle } from '../hooks/usePageTitle';
import { openPopoutWindow } from '../utils/popoutWindow';
import {
  HomeIcon,
  TruckIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  SunIcon,
  MoonIcon,
  XMarkIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
  DocumentDuplicateIcon,
  TableCellsIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  Cog6ToothIcon,
  SwatchIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  Squares2X2Icon,
  QueueListIcon,
  InformationCircleIcon,
  CircleStackIcon,
  UsersIcon,
  UserIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  TicketIcon,
  ClockIcon,
  BuildingStorefrontIcon,
  ArrowsRightLeftIcon,
  KeyIcon,
  DocumentMagnifyingGlassIcon,
  UserMinusIcon,
  AcademicCapIcon,
  WrenchScrewdriverIcon,
  MapIcon,
  EyeIcon,
  StarIcon,
  PencilSquareIcon,
  CheckIcon,
  GlobeAltIcon,
  ReceiptPercentIcon,
  NoSymbolIcon,
  PhotoIcon,
  InboxArrowDownIcon,
  BanknotesIcon,
  DocumentCurrencyDollarIcon,
  PlusIcon,
  LinkIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import styles from '../styles/CollapsibleDashboard.module.css';
import NavCountBadge from './NavCountBadge';
import { useSafetySystemEnabled } from '../hooks/useSafetySystemEnabled';
import { useOpenPingFeed } from '../hooks/useOpenPingFeed';
import { useOpenPingToasts } from '../hooks/useOpenPingToasts';
import RepairOrderToastPreferenceRow from './RepairOrderToastPreferenceRow';
import { Drawer } from './ui/Drawer';
import { DraggableModal } from './ui/DraggableModal';

// Separate component to isolate headerContent re-renders from the main layout
const HeaderContentSlot: React.FC = () => {
  const { headerContent } = useDispatchFooter();
  if (!headerContent) return null;
  return (
    <div className="hidden md:flex items-center">
      {/* Portal target for dispatch planner header */}
      {headerContent === 'dispatch-planner-header' ? (
        <div id="dispatch-planner-header-portal" />
      ) : (
        headerContent
      )}
    </div>
  );
};

/** Sample box for the field tint pickers. Reads the same CSS var the real
 *  fields do, so there's no second copy of the color math to drift. */
const FieldTintPreview: React.FC<{
  label: string;
  varName: string;
  darkVarName: string;
  darkMode: boolean;
}> = ({ label, varName, darkVarName, darkMode }) => (
  <div
    className="rounded-lg border border-theme-border-primary px-3 py-2 text-sm text-body"
    style={{ backgroundColor: `rgb(var(${darkMode ? darkVarName : varName}))` }}
  >
    {label} — 02/27/2026
  </div>
);

interface CollapsibleDashboardProps {
  children?: React.ReactNode;
}

function CollapsibleDashboard({ children }: CollapsibleDashboardProps) {
  const { instance, accounts } = useMsal();
  const currentUser = accounts[0];
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites, isFavorited, toggleFavorite } = useFavorites();
  const { order: savedNavOrder, groups: savedNavGroups, setOrder: saveNavOrder, setGroupOrder: saveNavGroupOrder, isLoading: navOrderLoading, isSaving: navOrderSaving } = useNavigationOrder();
  const [navEditMode, setNavEditMode] = useState(false);
  const { permissions, isInGroups, hasAccess } = usePermissions();
  const { permissionsMode, setPermissionsMode, enabledGroups } = usePermissionsMode();
  const { isOpen: isLegendOpen, openLegend, closeLegend, sections: legendSections, title: legendTitle } = useIconLegend();
  const { showSamsaraRouteEtas, setShowSamsaraRouteEtas, showExtendedWorkingStatus, setShowExtendedWorkingStatus, defaultSendToGwiMove, setDefaultSendToGwiMove, planningShowAllMoves, setPlanningShowAllMoves, autoCloseProOnSave, setAutoCloseProOnSave, showSaveAsDialog, setShowSaveAsDialog, compactFollowUpHandle, setCompactFollowUpHandle, hideFollowUpHandleWhenEmpty, setHideFollowUpHandleWhenEmpty } = useWorkingStatusPreference();
  const { productionReadonlyMode, setProductionReadonlyMode, canEnableMode } = useProductionReadonlyMode();

  // Outstanding maintenance requests: ONE poll feeding both the nav badge and
  // the persistent toasts. The hook self-gates on the maintenance audience, so
  // for everyone else this costs nothing and issues no request.
  // Notification preferences. Read HERE rather than with the other preference
  // hooks below because the toast hook on the next line needs the value.
  const {
    showDriverRequestToast,
    setShowDriverRequestToast,
    showOnlyMyDrivers,
    setShowOnlyMyDrivers,
    showRepairOrderToast,
    setShowRepairOrderToast,
  } = useNotificationPreferences();

  const openPingFeed = useOpenPingFeed();
  // Toasts only: the nav badge below keeps reading `openPingFeed` regardless,
  // so silencing the pop-ups never hides the queue itself.
  useOpenPingToasts(openPingFeed, showRepairOrderToast);

  /**
   * Count badges for leaf nav items, keyed by href. Deliberately a plain map
   * rather than a badge framework: the Repair Orders badge is the first one in
   * this nav, and a second one should be able to join by adding a line here.
   */
  const navBadges: Record<string, { count: number; unitLabel: string }> = {
    '/fleet-management/repair-orders': {
      count: openPingFeed.open,
      unitLabel: 'open maintenance requests',
    },
  };

  /** Badge for a submenu leaf, or null. Shared by the expanded submenu and the
   *  collapsed-rail flyout so the two cannot drift. */
  const renderNavBadge = (href?: string) => {
    const badge = href ? navBadges[href] : undefined;
    if (!badge) return null;
    return <NavCountBadge count={badge.count} unitLabel={badge.unitLabel} />;
  };

  // Check if we're in test environment
  const isTestEnvironment = import.meta.env.VITE_ENV === 'test';

  // Determine environment for banner
  const environment: 'test' | 'local' | null =
    import.meta.env.VITE_ENV === 'test' ? 'test' :
    (import.meta.env.VITE_ENV === 'local' || import.meta.env.DEV) ? 'local' :
    null;

  // Check if user is in Developer group (for permissions mode toggle)
  const isInDeveloperGroup = useMemo(() => {
    const GLOBAL_ADMINS = ['john.duplechain@gwii.com'];
    if (permissions?.user_email && GLOBAL_ADMINS.includes(permissions.user_email.toLowerCase())) {
      return true;
    }
    const userGroupIds = permissions?.groups || [];
    return enabledGroups.some(group =>
      userGroupIds.includes(group.group_id) &&
      (group.group_name?.toLowerCase().includes('developer') ||
       group.display_name?.toLowerCase().includes('developer'))
    );
  }, [permissions, enabledGroups]);

  // Safety System (V1 Safety port) release flag: gates the Hub / Units nav leaves.
  const safetySystemEnabled = useSafetySystemEnabled();

  // Load sidebar collapsed state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navSearchQuery, setNavSearchQuery] = useState('');

  // Load menu expanded states from localStorage and check current route
  const [fleetManagementExpanded, setFleetManagementExpanded] = useState(() => {
    // Check if we're currently on a fleet management route
    const isOnFleetManagementRoute = location.pathname.startsWith('/fleet-management');

    // Load saved state
    const saved = localStorage.getItem('fleetManagementExpanded');

    // If we're on a fleet management route, default to expanded
    // Otherwise use saved state or default to false
    return isOnFleetManagementRoute || saved === 'true';
  });
  const [safetyExpanded, setSafetyExpanded] = useState(() => {
    const isOnSafetyRoute = location.pathname.startsWith('/safety');
    const saved = localStorage.getItem('safetyExpanded');
    return isOnSafetyRoute || saved === 'true';
  });
  const [analyticsExpanded, setAnalyticsExpanded] = useState(() => {
    const isOnAnalyticsRoute = location.pathname.startsWith('/analytics');
    const saved = localStorage.getItem('analyticsExpanded');
    return isOnAnalyticsRoute || saved === 'true';
  });
  const [administrationExpanded, setAdministrationExpanded] = useState(() => {
    const isOnAdministrationRoute = location.pathname.startsWith('/administration');
    const saved = localStorage.getItem('administrationExpanded');
    return isOnAdministrationRoute || saved === 'true';
  });
  const [tracingExpanded, setTracingExpanded] = useState(() => {
    const isOnTracingRoute = location.pathname.startsWith('/tracing');
    const saved = localStorage.getItem('tracingExpanded');
    return isOnTracingRoute || saved === 'true';
  });
  const [compliancePortalExpanded, setCompliancePortalExpanded] = useState(() => {
    const isOnComplianceRoute = location.pathname.startsWith('/compliance');
    const saved = localStorage.getItem('compliancePortalExpanded');
    return isOnComplianceRoute || saved === 'true';
  });
  const [dispatchingExpanded, setDispatchingExpanded] = useState(() => {
    const isOnDispatchingRoute = location.pathname.startsWith('/dispatching');
    const saved = localStorage.getItem('dispatchingExpanded');
    return isOnDispatchingRoute || saved === 'true';
  });
  const [schedulingExpanded, setSchedulingExpanded] = useState(() => {
    const isOnSchedulingRoute = location.pathname.startsWith('/scheduling');
    const saved = localStorage.getItem('schedulingExpanded');
    return isOnSchedulingRoute || saved === 'true';
  });
  const [orderManagementExpanded, setOrderManagementExpanded] = useState(() => {
    const isOnOrderManagementRoute = location.pathname.startsWith('/order-management');
    const saved = localStorage.getItem('orderManagementExpanded');
    return isOnOrderManagementRoute || saved === 'true';
  });
  const [accountingExpanded, setAccountingExpanded] = useState(() => {
    const isOnAccountingRoute = location.pathname.startsWith('/accounting');
    const saved = localStorage.getItem('accountingExpanded');
    return isOnAccountingRoute || saved === 'true';
  });

  const [developmentExpanded, setDevelopmentExpanded] = useState(() => {
    const isOnDevelopmentRoute = location.pathname.startsWith('/development');
    const saved = localStorage.getItem('developmentExpanded');
    return isOnDevelopmentRoute || saved === 'true';
  });
  const [loggingExpanded, setLoggingExpanded] = useState(() => {
    const isOnLoggingRoute = location.pathname.startsWith('/logging');
    const saved = localStorage.getItem('loggingExpanded');
    return isOnLoggingRoute || saved === 'true';
  });
  const [geofencingExpanded, setGeofencingExpanded] = useState(() => {
    const isOnGeofencingRoute = location.pathname.startsWith('/geofencing');
    const saved = localStorage.getItem('geofencingExpanded');
    return isOnGeofencingRoute || saved === 'true';
  });
  const [yardManagementExpanded, setYardManagementExpanded] = useState(() => {
    const isOnYardManagementRoute = location.pathname.startsWith('/yard');
    const saved = localStorage.getItem('yardManagementExpanded');
    return isOnYardManagementRoute || saved === 'true';
  });
  const [researchingReportsExpanded, setResearchingReportsExpanded] = useState(() => {
    const isOnResearchingReportsRoute = location.pathname.startsWith('/researching-reports');
    const saved = localStorage.getItem('researchingReportsExpanded');
    return isOnResearchingReportsRoute || saved === 'true';
  });
  // CST section groups the Capacity Solutions Team reports. They keep their
  // existing /researching-reports/* routes (so grants are unaffected), so the
  // section's expand state keys off this explicit path list.
  const CST_PATHS = [
    '/researching-reports/home-depot-cst',
    '/researching-reports/cst',
    '/researching-reports/cst-detention-risk',
  ];
  const [cstExpanded, setCstExpanded] = useState(() => {
    const saved = localStorage.getItem('cstExpanded');
    return CST_PATHS.includes(location.pathname) || saved === 'true';
  });
  const [trainingExpanded, setTrainingExpanded] = useState(() => {
    const isOnTrainingRoute = location.pathname.startsWith('/training');
    const saved = localStorage.getItem('trainingExpanded');
    return isOnTrainingRoute || saved === 'true';
  });
  const [favoritesExpanded, setFavoritesExpanded] = useState(() => {
    const saved = localStorage.getItem('favoritesExpanded');
    return saved === null ? true : saved === 'true';
  });
  // Seeded from the real viewport rather than `false` so the Full-Slide gate below
  // is correct on the first paint, before the resize effect has run.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1024,
  );
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [popoutTop, setPopoutTop] = useState<number>(0);
  // Collapsed-rail nav search: hover reveals the flyout, clicking the magnifier
  // latches it open so a stray cursor move can't yank the input away mid-typing.
  const [collapsedSearchHovered, setCollapsedSearchHovered] = useState(false);
  const [collapsedSearchPinned, setCollapsedSearchPinned] = useState(false);
  const collapsedSearchInputRef = useRef<HTMLInputElement | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false);
  const [userHistoryModalOpen, setUserHistoryModalOpen] = useState(false);
  const [userEnabledGroups, setUserEnabledGroups] = useState<any[]>([]);
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);
  const [bugReportDrawerOpen, setBugReportDrawerOpen] = useState(false);
  const [bugReportDrawerTemporaryHidden, setBugReportDrawerTemporaryHidden] = useState(false);
  // Market Conditions: the header pill's popover, the market a toast click
  // asked to focus, and the Manage drawer. The push hook toasts every user when
  // a note is saved and opens the popover from the toast.
  const [marketConditionsOpen, setMarketConditionsOpen] = useState(false);
  const [marketConditionsFocus, setMarketConditionsFocus] = useState<string | null>(null);
  const [marketConditionsDrawerOpen, setMarketConditionsDrawerOpen] = useState(false);
  useMarketConditionsPush({
    enabled: true,
    onOpen: (market) => { setMarketConditionsFocus(market); setMarketConditionsOpen(true); },
  });
  const [featureRequestDrawerOpen, setFeatureRequestDrawerOpen] = useState(false);
  const [featureRequestDrawerTemporaryHidden, setFeatureRequestDrawerTemporaryHidden] = useState(false);
  const [helpdeskTicketDrawerOpen, setHelpdeskTicketDrawerOpen] = useState(false);
  const [helpdeskTicketDrawerTemporaryHidden, setHelpdeskTicketDrawerTemporaryHidden] = useState(false);
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [screenshotModalImage, setScreenshotModalImage] = useState<string | null>(null);
  const [screenshotUpdateCallback, setScreenshotUpdateCallback] = useState<((newScreenshot: string) => void) | null>(null);
  const [screenshotSource, setScreenshotSource] = useState<'bug' | 'feature' | 'helpdesk'>('bug');

  // The screenshot editor hides whichever ticket modal opened it; restore that one on close.
  const restoreTicketModalAfterScreenshot = () => {
    if (screenshotSource === 'bug') {
      setBugReportDrawerTemporaryHidden(false);
    } else if (screenshotSource === 'helpdesk') {
      setHelpdeskTicketDrawerTemporaryHidden(false);
    } else {
      setFeatureRequestDrawerTemporaryHidden(false);
    }
  };
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  // Get display preferences from context
  const {
    showTableGridlines,
    setShowTableGridlines,
    showHorizontalGridlines,
    setShowHorizontalGridlines,
    showVerticalGridlines,
    setShowVerticalGridlines,
    showTableZebraStriping,
    setShowTableZebraStriping,
    zebraColorLight,
    setZebraColorLight,
    zebraColorDark,
    setZebraColorDark,
    gridlineColorLight,
    setGridlineColorLight,
    gridlineColorDark,
    setGridlineColorDark,
    groupRowColorLight,
    setGroupRowColorLight,
    groupRowColorDark,
    setGroupRowColorDark,
    showColumnWidths,
    setShowColumnWidths,
    showGridHighlight,
    setShowGridHighlight,
    showGridLabels,
    setShowGridLabels,
    showGridBorders,
    setShowGridBorders,
    compactTableMode,
    setCompactTableMode,
    compactOrderDetails,
    setCompactOrderDetails,
    showFieldNames,
    setShowFieldNames,
    navFullSlide,
    setNavFullSlide,
    showInputPlaceholders,
    setShowInputPlaceholders,
    plainTextLockedFields,
    setPlainTextLockedFields,
    styleLockedFieldText,
    setStyleLockedFieldText,
    lockedFieldTextColorLight,
    setLockedFieldTextColorLight,
    lockedFieldTextColorDark,
    setLockedFieldTextColorDark,
    lockedFieldTextBold,
    setLockedFieldTextBold,
    lockedFieldTextItalic,
    setLockedFieldTextItalic,
    lockedFieldChromeOpacity,
    setLockedFieldChromeOpacity,
    tintEditableFields,
    setTintEditableFields,
    editableFieldTintLight,
    setEditableFieldTintLight,
    editableFieldTintDark,
    setEditableFieldTintDark,
    tintLockedFields,
    setTintLockedFields,
    lockedFieldTintLight,
    setLockedFieldTintLight,
    lockedFieldTintDark,
    setLockedFieldTintDark,
  } = useDisplayPreferences();

  // Theme preferences
  const {
    themeEnabled,
    setThemeEnabled,
    lightThemeColor,
    setLightThemeColor,
    darkThemeColor,
    setDarkThemeColor,
    darkPaletteVariant,
    setDarkPaletteVariant,
  } = useTheme();

  // Activity tracking: interval/visibility flushing + document click capture.
  useEffect(() => {
    activityTracker.start();

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.('button, a, [role="button"]');
      if (!el) return;
      const label =
        el.getAttribute('aria-label') ||
        (el.textContent || '').trim().slice(0, 80);
      if (!label) return;
      activityTracker.track({ event_type: 'click', target: label });
    };
    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, []);

  // Activity tracking: page views — emit the page just left with its dwell time.
  const activityPageRef = useRef<{ path: string; enteredAt: number } | null>(null);
  useEffect(() => {
    const prev = activityPageRef.current;
    const now = Date.now();
    if (prev && prev.path !== location.pathname) {
      activityTracker.track({
        event_type: 'page_view',
        page_path: prev.path,
        duration_ms: now - prev.enteredAt,
      });
    }
    if (!prev || prev.path !== location.pathname) {
      activityPageRef.current = { path: location.pathname, enteredAt: now };
    }
  }, [location.pathname]);

  // Fetch user profile from Microsoft Graph
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (currentUser) {
        try {
          const request = {
            scopes: ["User.Read"],
            account: currentUser
          };
          const response = await instance.acquireTokenSilent(request);

          // Fetch user profile from Microsoft Graph
          const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: {
              Authorization: `Bearer ${response.accessToken}`
            }
          });

          if (graphResponse.ok) {
            const profile = await graphResponse.json();
            setUserProfile(profile);
          }

          // Fetch user photo
          try {
            const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
              headers: {
                Authorization: `Bearer ${response.accessToken}`
              }
            });

            if (photoResponse.ok) {
              const blob = await photoResponse.blob();
              const photoUrl = URL.createObjectURL(blob);
              setUserPhotoUrl(photoUrl);
            }
          } catch (photoError) {
            // Photo might not exist, silently fail
          }
        } catch (error: any) {
          // Only log error if it's not an interaction required error
          if (!error?.errorCode?.includes('interaction_required')) {
          }
          // For interaction required errors, user needs to sign in again
          // This is handled by MSAL automatically when user interacts with the app
        }
      }
    };

    fetchUserProfile();

    // Cleanup blob URL on unmount
    return () => {
      if (userPhotoUrl) {
        URL.revokeObjectURL(userPhotoUrl);
      }
    };
  }, [currentUser, instance]);

  // Fetch enabled groups for user drawer
  useEffect(() => {
    const fetchEnabledGroups = async () => {
      try {
        const groups = await permissionsService.getEnabledGroups();
        setUserEnabledGroups(groups);
      } catch (error) {
        // Silently fail - groups section just won't show
      }
    };

    fetchEnabledGroups();
  }, []);

  // Check if we're on mobile and force sidebar to be expanded
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      // If we're switching to mobile and sidebar is collapsed, expand it
      // Mobile should always show full width when menu is open
      if (mobile && sidebarCollapsed) {
        // Don't actually change the collapsed state, just let CSS handle it
        // The mobile styles will override the collapsed state
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [sidebarCollapsed]);

  // Full-Slide navigation. Only meaningful on a collapsed desktop sidebar: below
  // 1024px the responsive CSS turns the nav into an off-canvas drawer that already
  // shows labels, and a pinned-open nav has nothing to slide.
  const fullSlideEnabled = navFullSlide && sidebarCollapsed && !mobileMenuOpen && !isMobile;
  const { slidOut, navHandlers, collapseAfterNavigate } = useNavFullSlide({
    enabled: fullSlideEnabled,
  });

  // The persisted pin still drives the grid track (so page content never reflows),
  // while everything the nav *renders* keys off this instead.
  const effectiveCollapsed = sidebarCollapsed && !slidOut;

  // The collapsed icon rail hides the "Filter Navigation" box, so it gets a magnifier
  // that opens the filter in a flyout. Only in pop-out mode: with Full-Slide on, the
  // rail is unreachable by hover (hovering slides the whole nav out, which already
  // shows the real search box).
  // Desktop-only, same as Full-Slide: below 1024px the nav is a 280px off-canvas
  // drawer, where a flyout offset to left:64px would land on top of its own content.
  const showCollapsedSearch =
    effectiveCollapsed && !navFullSlide && !mobileMenuOpen && !navEditMode && !isMobile;
  const collapsedSearchOpen = showCollapsedSearch && (collapsedSearchHovered || collapsedSearchPinned);

  // Focus the input as soon as the flyout appears so the user can just start typing.
  useEffect(() => {
    if (collapsedSearchOpen) collapsedSearchInputRef.current?.focus();
  }, [collapsedSearchOpen]);

  // Leaving rail mode (pinning the nav open, going mobile, enabling Full-Slide) drops
  // the latch so the flyout can't reappear somewhere it doesn't belong.
  useEffect(() => {
    if (showCollapsedSearch) return;
    setCollapsedSearchHovered(false);
    setCollapsedSearchPinned(false);
  }, [showCollapsedSearch]);

  // Load theme from localStorage or default to light mode.
  // The bootstrap script in index.html already applied this class before first
  // paint from the same key, so this is a no-op on load rather than the first
  // application - it stays because React still owns the toggle.
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme === 'dark'; // Default to light if no saved preference

    // Apply initial theme to document element
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    return isDark;
  });

  // Save theme to localStorage and update document element whenever it changes
  useEffect(() => {
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');

    // Update the dark class on the document element for Tailwind CSS
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Save sidebar collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Save menu expanded states to localStorage
  useEffect(() => {
    localStorage.setItem('fleetManagementExpanded', fleetManagementExpanded.toString());
  }, [fleetManagementExpanded]);

  useEffect(() => {
    localStorage.setItem('safetyExpanded', safetyExpanded.toString());
  }, [safetyExpanded]);

  useEffect(() => {
    localStorage.setItem('analyticsExpanded', analyticsExpanded.toString());
  }, [analyticsExpanded]);

  useEffect(() => {
    localStorage.setItem('administrationExpanded', administrationExpanded.toString());
  }, [administrationExpanded]);

  useEffect(() => {
    localStorage.setItem('tracingExpanded', tracingExpanded.toString());
  }, [tracingExpanded]);

  useEffect(() => {
    localStorage.setItem('compliancePortalExpanded', compliancePortalExpanded.toString());
  }, [compliancePortalExpanded]);

  useEffect(() => {
    localStorage.setItem('dispatchingExpanded', dispatchingExpanded.toString());
  }, [dispatchingExpanded]);

  useEffect(() => {
    localStorage.setItem('schedulingExpanded', schedulingExpanded.toString());
  }, [schedulingExpanded]);

  useEffect(() => {
    localStorage.setItem('orderManagementExpanded', orderManagementExpanded.toString());
  }, [orderManagementExpanded]);

  useEffect(() => {
    localStorage.setItem('accountingExpanded', accountingExpanded.toString());
  }, [accountingExpanded]);

  useEffect(() => {
    localStorage.setItem('developmentExpanded', developmentExpanded.toString());
  }, [developmentExpanded]);

  useEffect(() => {
    localStorage.setItem('loggingExpanded', loggingExpanded.toString());
  }, [loggingExpanded]);

  useEffect(() => {
    localStorage.setItem('geofencingExpanded', geofencingExpanded.toString());
  }, [geofencingExpanded]);

  useEffect(() => {
    localStorage.setItem('yardManagementExpanded', yardManagementExpanded.toString());
  }, [yardManagementExpanded]);

  useEffect(() => {
    localStorage.setItem('researchingReportsExpanded', researchingReportsExpanded.toString());
  }, [researchingReportsExpanded]);

  useEffect(() => {
    localStorage.setItem('cstExpanded', cstExpanded.toString());
  }, [cstExpanded]);

  useEffect(() => {
    localStorage.setItem('trainingExpanded', trainingExpanded.toString());
  }, [trainingExpanded]);

  useEffect(() => {
    localStorage.setItem('favoritesExpanded', favoritesExpanded.toString());
  }, [favoritesExpanded]);

  // Auto-expand menu when navigating to a submenu route.
  // Note: This is intentional URL synchronization, not derived state. The pattern allows users
  // to manually collapse menus while on a route (it only expands if not already expanded).
  // Dependencies intentionally limited to pathname to run only on navigation, not on menu state changes.
  useEffect(() => {
    if (sidebarCollapsed) return; // Don't auto-expand when sidebar is collapsed

    const path = location.pathname;
    if (path.startsWith('/fleet-management') && !fleetManagementExpanded) setFleetManagementExpanded(true);
    if (path.startsWith('/safety') && !safetyExpanded) setSafetyExpanded(true);
    if (path.startsWith('/analytics') && !analyticsExpanded) setAnalyticsExpanded(true);
    if (path.startsWith('/administration') && !administrationExpanded) setAdministrationExpanded(true);
    if (path.startsWith('/compliance') && !compliancePortalExpanded) setCompliancePortalExpanded(true);
    if (path.startsWith('/dispatching') && !dispatchingExpanded) setDispatchingExpanded(true);
    if (path.startsWith('/scheduling') && !schedulingExpanded) setSchedulingExpanded(true);
    if (path.startsWith('/order-management') && !orderManagementExpanded) setOrderManagementExpanded(true);
    if (path.startsWith('/accounting') && !accountingExpanded) setAccountingExpanded(true);
    if (path.startsWith('/development') && !developmentExpanded) setDevelopmentExpanded(true);
    if (path.startsWith('/logging') && !loggingExpanded) setLoggingExpanded(true);
    if (path.startsWith('/geofencing') && !geofencingExpanded) setGeofencingExpanded(true);
    if (path.startsWith('/yard') && !yardManagementExpanded) setYardManagementExpanded(true);
    if (path.startsWith('/researching-reports') && !researchingReportsExpanded) setResearchingReportsExpanded(true);
    if (CST_PATHS.includes(path) && !cstExpanded) setCstExpanded(true);
    if (path.startsWith('/training') && !trainingExpanded) setTrainingExpanded(true);
    if (path.startsWith('/tracing') && !tracingExpanded) setTracingExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, sidebarCollapsed]);

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  const navigation = [
    {
      name: 'Home',
      icon: HomeIcon,
      href: '/home'
    },
    {
      name: 'Roadmap',
      icon: MapIcon,
      href: '/roadmap'
    },
    {
      name: 'Fleet Management',
      icon: TruckIcon,
      expandable: true,
      expanded: fleetManagementExpanded,
      submenu: [
        { name: 'Live Map', icon: MapPinIcon, href: '/fleet-management/live-map' },
        { name: 'Chassis Map', icon: MapPinIcon, href: '/fleet-management/chassis-map' },
        { name: 'Equipment Report', icon: ClipboardDocumentListIcon, href: '/fleet-management/equipment-report' },
        { name: 'Unmapped Truck Tags', icon: WrenchScrewdriverIcon, href: '/fleet-management/truck-tag-mapping' },
        { name: 'Drivers', icon: UsersIcon, href: '/fleet-management/drivers' },
        { name: 'Driver Data Issues', icon: ExclamationTriangleIcon, href: '/fleet-management/vin-mismatch' },
        { name: 'VIN Overrides', icon: WrenchScrewdriverIcon, href: '/fleet-management/vin-overrides' },
        { name: 'Fuel Cards', icon: BanknotesIcon, href: '/fleet-management/fuel-cards' },
        { name: 'Driver Status Monitor', icon: UsersIcon, href: '/fleet-management/driver-status-monitor' },
        { name: 'Repair Orders', icon: ClipboardDocumentListIcon, href: '/fleet-management/repair-orders' },
      ]
    },
    {
      name: 'Dispatching',
      icon: ContainerIcon,
      expandable: true,
      expanded: dispatchingExpanded,
      submenu: [
        { name: 'Dispatch Dashboard', icon: DashboardPanelIcon, href: '/dispatching/dispatch-dashboard' },
        { name: 'Dispatch Planner', icon: PlannerIcon, href: '/dispatching/dispatch-planner' },
        { name: 'Smart Dispatch', icon: SparklesIcon, href: '/dispatching/smart-dispatch' },
        { name: 'Smart Preplanning', icon: CalendarDaysIcon, href: '/dispatching/smart-preplanning' },
        { name: 'Smart Preplan v2', icon: CalendarDaysIcon, href: '/dispatching/smart-preplanning-v2', permission: '/dispatching/smart-preplanning' },
        { name: 'Smart Preplan Review', icon: CalendarDaysIcon, href: '/dispatching/smart-preplan-review', permission: '/dispatching/smart-preplanning' },
        { name: 'At Risk Monitor', icon: ExclamationTriangleIcon, href: '/dispatching/at-risk-monitor' },
        { name: 'OTR By Location', icon: MapIcon, href: '/dispatching/otr-by-location' },
        { name: 'Driver Status Board', icon: ClipboardDocumentListIcon, href: '/dispatching/driver-status-board' },
        { name: 'Dispatch Visibility', icon: EyeIcon, href: '/dispatching/dispatch-visibility' },
        { name: 'Street Turn Opportunities', icon: ArrowsRightLeftIcon, href: '/dispatching/street-turn-opportunities' },
        { name: 'Driver Dispatch History', icon: ClockIcon, href: '/dispatching/driver-dispatch-history' },
      ]
    },
    {
      name: 'Order Management',
      icon: ReceiptPercentIcon,
      expandable: true,
      expanded: orderManagementExpanded,
      submenu: [
        { name: 'Quotes', icon: DocumentTextIcon, href: '/order-management/quotes' },
        { name: 'Templates', icon: DocumentDuplicateIcon, href: '/order-management/templates' },
        { name: 'Create New Order', icon: PlusIcon, href: '/order-management/create-order' },
        // Piggybacks the Pro-page edit permission (order editors) rather than a
        // new page permission — see the tool's route gate in DashboardLayout.
        { name: 'Order Update Import', icon: ArrowUpTrayIcon, href: '/order-management/order-update-import', permission: '/pro/:pronumber' },
        { name: 'Home Depot OTW Upload', icon: ArrowUpTrayIcon, href: '/order-management/home-depot-otw-upload' },
        { name: 'Dal Tile OTW Upload', icon: ArrowUpTrayIcon, href: '/order-management/dal-tile-otw-upload' },
        { name: 'LC Dashboards', icon: DashboardPanelIcon, href: '/order-management/lc-dashboards' },
        { name: 'Warehouse Photos', icon: PhotoIcon, href: '/order-management/warehouse-photos' },
        { name: 'Pending EDI Orders', icon: InboxArrowDownIcon, href: '/order-management/pending-edi-orders' },
        { name: 'System Events', icon: ClockIcon, href: '/order-management/event-history' },
      ]
    },
    {
      // Accounting — the invoicing side. EDI Pending Invoices is the entry point into
      // invoice generation and sending; more of that workflow lands here.
      name: 'Accounting',
      icon: BanknotesIcon,
      expandable: true,
      expanded: accountingExpanded,
      submenu: [
        { name: 'Accounting Dashboards', icon: DashboardPanelIcon, href: '/accounting/dashboards' },
        { name: 'EDI Pending Invoices', icon: DocumentCurrencyDollarIcon, href: '/accounting/pending-edi-invoices' },
        { name: 'Accessorial Upload', icon: ArrowUpTrayIcon, href: '/accounting/accessorial-upload' },
        { name: 'Invoice Lag', icon: ClockIcon, href: '/accounting/reports/invoice-lag' },
        { name: 'Invoicing Reports', icon: DocumentChartBarIcon, href: '/accounting/reports/average-days-to-invoice' },
        { name: 'XLS Invoice', icon: ArrowDownTrayIcon, href: '/accounting/reports/xls-invoice' },
        { name: 'Sage Export Details', icon: DocumentChartBarIcon, href: '/accounting/reports/sage-export-details' },
        { name: 'Credit Memo Report', icon: DocumentChartBarIcon, href: '/accounting/reports/credit-memo' },
      ]
    },
    {
      name: 'Scheduling',
      icon: CalendarDaysIcon,
      expandable: true,
      expanded: schedulingExpanded,
      submenu: [
        { name: 'Scheduler', icon: CalendarDaysIcon, href: '/scheduling/scheduler' },
        { name: 'Request Capacity', icon: CalendarDaysIcon, href: '/scheduling/request-capacity', unrestricted: true },
        { name: 'Customer Scheduling', icon: CalendarDaysIcon, href: '/scheduling/customer-scheduling' },
        { name: 'Logistics Coordinator', icon: ClipboardDocumentListIcon, href: '/scheduling/logistics-coordinator' },
        { name: 'TTs & Service Failures', icon: ExclamationTriangleIcon, href: '/scheduling/tts-and-service-failures' },
        { name: 'History', icon: ClockIcon, href: '/scheduling/history' },
      ]
    },
    {
      name: 'Safety',
      icon: ShieldCheckIcon,
      expandable: true,
      expanded: safetyExpanded,
      submenu: [
        ...(safetySystemEnabled ? [
          { name: 'Hub', icon: HomeIcon, href: '/safety/hub' },
          { name: 'Units', icon: TruckIcon, href: '/safety/units' },
          { name: 'Tickets', icon: TicketIcon, href: '/safety/tickets' },
          { name: 'Maintenance Reports', icon: WrenchScrewdriverIcon, href: '/safety/maintenance-reports' },
          { name: 'Incidents', icon: ExclamationTriangleIcon, href: '/safety/incidents' },
          { name: 'Report Center', icon: DocumentChartBarIcon, href: '/safety/report-center' },
          { name: 'Safety Tables', icon: TableCellsIcon, href: '/safety/tables' },
        ] : []),
        { name: 'Driver Scores', icon: ChartBarIcon, href: '/safety/driver-scores' },
        { name: 'Drivers', icon: UsersIcon, href: `${config.V2_URL}/safety/drivers?statuses=0&statuses=1&statuses=2&multiSearch=false`, external: true },
        { name: 'OOS Deferments', icon: ClipboardDocumentListIcon, href: '/safety/oos-deferments' },
        { name: 'Hours of Service', icon: ClockIcon, href: '/safety/hours-of-service' },
      ]
    },
    {
      name: 'Analytics',
      icon: AnalyticsIcon,
      expandable: true,
      expanded: analyticsExpanded,
      submenu: [
        { name: 'Rejection Analytics', icon: ChartBarIcon, href: '/analytics/rejection-analytics' },
        { name: 'Aircall', icon: AircallIcon, href: '/analytics/aircall' },
      ]
    },
    {
      // Single hub link — the per-table pages live on the /data-management hub
      // page, grouped and searchable, rather than as individual nav leaves.
      name: 'Table Maintenance',
      icon: DataManagementIcon,
      href: '/data-management',
    },
    {
      name: 'Development',
      icon: DevelopmentIcon,
      expandable: true,
      expanded: developmentExpanded,
      submenu: [
        { name: 'Component Library', icon: Squares2X2Icon, href: '/development/component-library' },
        { name: 'DevOps Kanban', icon: Squares2X2Icon, href: '/development/devops-kanban' },
        { name: 'DevOps Backlog', icon: QueueListIcon, href: '/development/devops-backlog' },
        { name: 'Samsara API', icon: ApiIcon, href: '/development/samsara-api' },
        { name: 'Database Monitor', icon: CircleStackIcon, href: '/development/database-monitor' },
        { name: 'Software Inventory', icon: ClipboardDocumentListIcon, href: '/development/software-inventory' },
      ]
    },
    {
      name: 'Logging',
      icon: ClipboardDocumentListIcon,
      expandable: true,
      expanded: loggingExpanded,
      submenu: [
        { name: 'System Logs', icon: DocumentTextIcon, href: '/logging/system-logs' },
        { name: 'Audit Logs', icon: ShieldCheckIcon, href: '/logging/audit-logs' },
        { name: 'Security Audit', icon: ShieldExclamationIcon, href: '/logging/security-audit' },
        { name: 'Impersonation Activity', icon: UsersIcon, href: '/logging/impersonation-activity' },
        { name: 'Permission Errors', icon: ExclamationCircleIcon, href: '/logging/permission-errors' },
      ]
    },
    {
      name: 'Geofencing',
      icon: MapPinIcon,
      expandable: true,
      expanded: geofencingExpanded,
      submenu: [
        { name: 'Driver Travel History', icon: MapPinIcon, href: '/geofencing/driver-travel-history' },
        { name: 'Location Accuracy', icon: MapPinIcon, href: '/geofencing/geofence-location-accuracy' },
        { name: 'Single-dispatch Units', icon: MapPinIcon, href: '/geofencing/single-dispatch-units' },
      ]
    },
    {
      name: 'Yard Management',
      icon: BuildingStorefrontIcon,
      expandable: true,
      expanded: yardManagementExpanded,
      submenu: [
        { name: 'Reefer Log', icon: DocumentTextIcon, href: '/yard/reefer-log' },
        { name: 'Update Yard Inventory', icon: ClipboardDocumentListIcon, href: '/yard/inventory' },
        { name: 'Yard Report', icon: ChartBarIcon, href: '/yard/reports' },
        { name: 'Yard Inventory History', icon: ClockIcon, href: '/yard/inventory-history' },
        { name: 'Yard Inventory Locations', icon: MapPinIcon, href: '/yard/locations' },
        { name: 'Yard Accounts', icon: KeyIcon, href: '/yard/accounts' },
      ]
    },
    {
      name: 'Researching / Reports',
      icon: DocumentMagnifyingGlassIcon,
      expandable: true,
      expanded: researchingReportsExpanded,
      submenu: [
        { name: 'Declined Dispatches', icon: ExclamationCircleIcon, href: '/researching-reports/declined-dispatches' },
        { name: 'Deadline Dispatch Removals', icon: UserMinusIcon, href: '/researching-reports/dispatch-removals' },
        { name: 'Dispatch Override Approvals', icon: ShieldCheckIcon, href: '/researching-reports/dispatch-override-approvals' },
        { name: 'TTs & Service Failures', icon: DocumentChartBarIcon, href: '/researching-reports/tts-service-failures' },
        { name: 'Recoded Service Failures', icon: DocumentChartBarIcon, href: '/researching-reports/recoded-service-failures' },
        { name: 'gwiTrace Progress', icon: ArrowTrendingUpIcon, href: '/researching-reports/gwitrace-progress' },
        { name: 'Dispatcher Metrics', icon: ChartBarIcon, href: '/researching-reports/dispatcher-metrics' },
        { name: 'Executive Report', icon: ChartBarIcon, href: '/researching-reports/executive-report' },
        { name: 'Win the Day', icon: ChartBarIcon, href: '/researching-reports/win-the-day' },
        { name: 'Executive Win the Day', icon: ChartBarIcon, href: '/researching-reports/executive-win-the-day' },
        { name: 'Win the Day Settings', icon: ChartBarIcon, href: '/researching-reports/win-the-day-settings' },
        { name: 'Container Status Report', icon: DocumentChartBarIcon, href: '/researching-reports/container-status-report' },
        { name: 'Dispatcher Street Turn Counts', icon: ArrowsRightLeftIcon, href: '/researching-reports/dispatcher-street-turns' },
        { name: 'Intelligence Report', icon: DocumentChartBarIcon, href: '/researching-reports/intelligence-report' },
        { name: 'LC Metrics', icon: ChartBarIcon, href: '/researching-reports/lc-metrics' },
        { name: 'Unbilled Charges', icon: ReceiptPercentIcon, href: '/researching-reports/unbilled-charges' },
        { name: 'Invoice Reconciliation', icon: ReceiptPercentIcon, href: '/researching-reports/invoice-estimate' },
        { name: 'Invoice Overrides', icon: NoSymbolIcon, href: '/researching-reports/invoice-overrides' },
        { name: 'Appointment Snapshots', icon: CalendarDaysIcon, href: '/researching-reports/appointment-snapshots' },
        { name: 'User History', icon: ClockIcon, href: '/researching-reports/user-history' },
      ]
    },
    {
      name: 'CST',
      icon: ClockIcon,
      expandable: true,
      expanded: cstExpanded,
      submenu: [
        { name: 'CST', icon: BuildingStorefrontIcon, href: '/researching-reports/cst' },
        { name: 'CST Detention Risk', icon: ClockIcon, href: '/researching-reports/cst-detention-risk' },
        { name: 'Home Depot - CST', icon: BuildingStorefrontIcon, href: '/researching-reports/home-depot-cst' },
      ]
    },
    {
      name: 'Training',
      icon: AcademicCapIcon,
      expandable: true,
      expanded: trainingExpanded,
      submenu: [
        { name: 'Driver Management', icon: DocumentTextIcon, href: '/training/driver-management' },
        { name: 'Working the Boards', icon: DocumentTextIcon, href: '/training/dispatching/boards' },
        { name: 'AI-Assisted Dispatch', icon: DocumentTextIcon, href: '/training/dispatching/ai' },
        { name: 'Optimization & Visibility', icon: DocumentTextIcon, href: '/training/dispatching/optimization' },
      ]
    },
    {
      name: 'Administration',
      icon: Cog6ToothIcon,
      expandable: true,
      expanded: administrationExpanded,
      submenu: [
        { name: 'Permissions', icon: ShieldCheckIcon, href: '/administration/permissions' },
        { name: 'Users', icon: UsersIcon, href: '/administration/users' },
        { name: 'AI Management', icon: SparklesIcon, href: '/administration/ai-management' },
        { name: 'Plan your day requests', icon: SparklesIcon, href: '/administration/preplan-requests' },
        { name: 'gwiMove Activity', icon: ClipboardDocumentListIcon, href: '/administration/gwimove-activity' },
        { name: 'Managed Logins', icon: KeyIcon, href: '/administration/managed-logins' },
        { name: 'API Customers', icon: KeyIcon, href: '/administration/api-customers' },
        { name: 'Axle Push', icon: PaperAirplaneIcon, href: '/administration/axle-push' },
        { name: 'Application Settings', icon: Cog6ToothIcon, href: '/administration/application-settings' },
        { name: 'Customize', icon: SwatchIcon, href: '/administration/customize' },
        { name: 'Manage Roadmap', icon: MapIcon, href: '/administration/manage-roadmap' },
        { name: 'System Diagnostics', icon: WrenchScrewdriverIcon, href: '/administration/diagnostics' },
        { name: 'Quote Migration', icon: DocumentTextIcon, href: '/administration/quote-migration' },
        { name: 'Saverules Dashboard', icon: ChartBarIcon, href: '/administration/saverules-dashboard' },
      ]
    },
    {
      name: 'Tracing',
      icon: GlobeAltIcon,
      expandable: true,
      expanded: tracingExpanded,
      submenu: [
        // `order` pins the three switches an operator reaches for daily:
        // turn a direction on, then go look at what it linked. The rest stay
        // alphabetical (no `order`), so a new page still needs no decision.
        // NOTE the route stays /tracing/allowed-carriers -- renaming the page
        // does not break anyone's bookmark or the PermissionGate route key.
        { name: 'Import Carriers', icon: ShieldCheckIcon, href: '/tracing/allowed-carriers', order: 1 },
        { name: 'Export Carriers', icon: ShieldCheckIcon, href: '/tracing/export-carriers', order: 2 },
        { name: 'Tracking Links', icon: LinkIcon, href: '/tracing/tracking-links', order: 3 },
        { name: 'CSX Terminals', icon: MapPinIcon, href: '/tracing/csx-terminals' },
        { name: 'Not Tracking', icon: ExclamationTriangleIcon, href: '/tracing/not-tracking' },
        { name: 'Untracked CSX', icon: MapPinIcon, href: '/tracing/untracked-csx' },
        { name: 'Webhook Activity', icon: ClockIcon, href: '/tracing/webhook-activity' },
      ]
    },
    {
      name: 'Compliance Portal',
      icon: ShieldCheckIcon,
      expandable: true,
      expanded: compliancePortalExpanded,
      submenu: [
        { name: 'Contracts', icon: DocumentTextIcon, href: '/compliance/contracts' },
        { name: 'Vendor Risk (TPRM)', icon: ShieldExclamationIcon, href: '/compliance/tprm' },
        { name: 'Reminders', icon: ClockIcon, href: '/compliance/reminders' },
        { name: 'Audit Log', icon: ClipboardDocumentListIcon, href: '/compliance/audit' },
        { name: 'Release Notes', icon: InformationCircleIcon, href: '/compliance/changelog' },
        { name: 'Settings', icon: Cog6ToothIcon, href: '/compliance/settings' },
      ]
    },
  ];

  const currentNavLeaf = useMemo(
    () => findNavItemByHref(navigation, location.pathname),
    [location.pathname],
  );
  // What the star saves: the sidebar leaf, or — on a Data Management table, which is
  // reached through the hub and so is never a sidebar leaf — the registry entry.
  const currentFavoritable = useMemo(
    () => resolveFavoritable(navigation, location.pathname),
    [location.pathname],
  );
  const [favoriteTab, setFavoriteTab] = useState<{ path: string; tab: string; label: string } | null>(null);
  const reportFavoriteTab = React.useCallback((tab: string, label: string) => {
    setFavoriteTab(previous => previous?.path === location.pathname && previous.tab === tab && previous.label === label
      ? previous : { path: location.pathname, tab, label });
  }, [location.pathname]);
  const favoriteTabContext = useMemo(() => ({
    navigationKey: location.key,
    requested: new URLSearchParams(location.search).get(FAVORITE_TAB_PARAM),
    report: reportFavoriteTab,
  }), [location.key, location.search, reportFavoriteTab]);
  const currentFavorite = currentFavoritable ? favoriteTarget(
    currentFavoritable,
    location.search,
    favoriteTab?.path === location.pathname ? favoriteTab : null,
  ) : null;
  const currentFavoriteHref = currentFavorite?.href ?? '';
  const canFavoriteCurrent = currentFavoritable !== null;
  // Existing page-only favorites remain removable after tabs gain deep links.
  const favoriteToggleHref = currentFavoritable && !isFavorited(currentFavoriteHref) && isFavorited(currentFavoritable.href)
    ? currentFavoritable.href : currentFavoriteHref;
  const currentIsFavorited = currentFavoritable !== null && isFavorited(favoriteToggleHref);

  // Reflect the current page in the browser tab title so users can identify tabs at
  // a glance. The sidebar leaf names the page when there is one; everything reached
  // WITHOUT a sidebar entry (Data Management's 106 tables, PRO/order details, vendor
  // pages) falls back to pageTitle.ts's resolvers.
  usePageTitle(currentNavLeaf?.name ?? null);

  // Filter navigation items based on user permissions - memoized for performance
  // This only recalculates when permissions change, not when menus expand/collapse
  const baseFilteredNavigation = useMemo(() => {
    if (!permissions) {
      // If permissions haven't loaded yet, show ALL navigation to prevent redirect loops
      // This ensures routes remain accessible while permissions are loading
      return navigation;
    }

    // Global admins - always have full access to all navigation
    const GLOBAL_ADMINS = ['john.duplechain@gwii.com'];
    if (permissions.user_email && GLOBAL_ADMINS.includes(permissions.user_email.toLowerCase())) {
      return navigation;
    }

    // If user has no groups and no accessible routes, show all navigation
    // (permissions system not configured yet)
    if (permissions.groups.length === 0 && permissions.accessible_routes.length === 0) {
      return navigation;
    }

    // Otherwise, filter based on hasAccess
    return navigation
      .map((item: any) => {
        if (item.expandable && item.submenu) {
          const submenu = item.submenu as any[];
          // External items (hard links to other apps) aren't permissioned routes,
          // so they can't grant access on their own. A section is only shown if the
          // user can reach at least one real route in it; external links then ride
          // along with that section's visibility rather than forcing it open.
          // A leaf may piggyback a different permission than its href (e.g. a
          // tool gated by an existing page permission but living at its own
          // path) via an optional `permission` route; falls back to href.
          const leafRoute = (subItem: any) => subItem.permission ?? subItem.href;
          const hasAccessibleRoute = submenu.some(
            (subItem: any) => !subItem.external && (subItem.unrestricted || hasAccess(leafRoute(subItem))),
          );
          if (hasAccessibleRoute) {
            return {
              ...item,
              submenu: submenu.filter(
                (subItem: any) => subItem.external || subItem.unrestricted || hasAccess(leafRoute(subItem)),
              ),
            };
          }
          return null;
        } else if (item.href === '/data-management') {
          // Hub link: visible if the user can reach the hub itself OR any
          // Data Management table route (the hub self-filters its cards).
          const canSeeAny =
            hasAccess('/data-management') ||
            (permissions.accessible_routes || []).some((r: string) =>
              r.startsWith('/data-management/'),
            );
          return canSeeAny ? item : null;
        } else if (item.href) {
          // For non-expandable items, check if user has access
          return hasAccess(item.href) ? item : null;
        }
        return item;
      })
      .filter((item: any) => item !== null) as any[];
  // safetySystemEnabled resolves asynchronously after mount; without it in the
  // deps the Safety Hub/Units leaves only appear after the next permissions refresh.
  }, [permissions, hasAccess, safetySystemEnabled]);

  // Merge expanded states with filtered navigation (lightweight operation)
  // This recalculates when menus expand/collapse, but it's just updating properties
  const filteredNavigation = useMemo(() => {
    return baseFilteredNavigation.map((item: any) => {
      if (item.name === 'Fleet Management') {
        return { ...item, expanded: fleetManagementExpanded };
      } else if (item.name === 'Safety') {
        return { ...item, expanded: safetyExpanded };
      } else if (item.name === 'Analytics') {
        return { ...item, expanded: analyticsExpanded };
      } else if (item.name === 'Dispatching') {
        return { ...item, expanded: dispatchingExpanded };
      } else if (item.name === 'Scheduling') {
        return { ...item, expanded: schedulingExpanded };
      } else if (item.name === 'Order Management') {
        return { ...item, expanded: orderManagementExpanded };
      } else if (item.name === 'Accounting') {
        return { ...item, expanded: accountingExpanded };
      } else if (item.name === 'Development') {
        return { ...item, expanded: developmentExpanded };
      } else if (item.name === 'Logging') {
        return { ...item, expanded: loggingExpanded };
      } else if (item.name === 'Geofencing') {
        return { ...item, expanded: geofencingExpanded };
      } else if (item.name === 'Yard Management') {
        return { ...item, expanded: yardManagementExpanded };
      } else if (item.name === 'Researching / Reports') {
        return { ...item, expanded: researchingReportsExpanded };
      } else if (item.name === 'CST') {
        return { ...item, expanded: cstExpanded };
      } else if (item.name === 'Training') {
        return { ...item, expanded: trainingExpanded };
      } else if (item.name === 'Administration') {
        return { ...item, expanded: administrationExpanded };
      } else if (item.name === 'Tracing') {
        return { ...item, expanded: tracingExpanded };
      } else if (item.name === 'Compliance Portal') {
        return { ...item, expanded: compliancePortalExpanded };
      }
      return item;
    });
  }, [baseFilteredNavigation, fleetManagementExpanded, dispatchingExpanded, schedulingExpanded, orderManagementExpanded, accountingExpanded, safetyExpanded, analyticsExpanded, administrationExpanded, tracingExpanded, compliancePortalExpanded, developmentExpanded, loggingExpanded, geofencingExpanded, yardManagementExpanded, researchingReportsExpanded, cstExpanded, trainingExpanded]);

  // Saved submenu orders override the default explicit/alphabetical order.
  const orderedFavorites = useMemo(
    () => applyNavOrder(favorites, savedNavGroups.favorites ?? [], fav => fav.href),
    [favorites, savedNavGroups],
  );
  const orderedNavigation = useMemo(
    () =>
      applyNavOrder(filteredNavigation, savedNavOrder).map((item: any) =>
        item.submenu
          ? {
              ...item,
              submenu: applyNavOrder([...item.submenu].sort((a: any, b: any) => {
                const ra = a.order ?? Number.POSITIVE_INFINITY;
                const rb = b.order ?? Number.POSITIVE_INFINITY;
                if (ra !== rb) return ra - rb;
                return a.name.localeCompare(b.name);
              }), savedNavGroups[item.name] ?? []),
            }
          : item,
      ),
    [filteredNavigation, savedNavOrder, savedNavGroups],
  );

  // Filter navigation by search query
  const searchFilteredNavigation = useMemo(() => {
    const query = navSearchQuery.trim().toLowerCase();
    if (!query) return orderedNavigation;

    return orderedNavigation
      .map((item: any) => {
        const parentMatches = item.name.toLowerCase().includes(query);

        if (item.expandable && item.submenu) {
          const matchingSubs = item.submenu.filter((sub: any) =>
            sub.name.toLowerCase().includes(query)
          );

          // If parent name matches, show all its children
          if (parentMatches) {
            return { ...item, expanded: true };
          }
          // If any children match, show parent with only those children
          if (matchingSubs.length > 0) {
            return { ...item, expanded: true, submenu: matchingSubs };
          }
          return null;
        }

        // Data Management hub: the per-table pages aren't permanent nav leaves,
        // but they stay findable here — a matching query surfaces the accessible,
        // wired tables as result rows under the (temporarily expanded) hub item.
        if (item.href === '/data-management') {
          const tableMatches = DATA_TABLES.filter(
            (t) => isTableReady(t) && hasAccess(t.route) && t.label.toLowerCase().includes(query),
          ).sort((a, b) => a.label.localeCompare(b.label));
          if (tableMatches.length > 0) {
            return {
              ...item,
              expandable: true,
              expanded: true,
              submenu: tableMatches.map((t) => ({
                name: t.label,
                icon: DataManagementIcon,
                href: t.route,
              })),
            };
          }
          return parentMatches ? item : null;
        }

        // Non-expandable items (like Home)
        return parentMatches ? item : null;
      })
      .filter(Boolean);
  }, [orderedNavigation, navSearchQuery, hasAccess]);

  // Get breadcrumb items based on current path - memoized for performance
  const breadcrumbItems = useMemo(() => {
    const path = location.pathname;

    // Check if we're in a submenu route
    if (path.startsWith('/fleet-management/')) {
      const fleetManagementItem = navigation.find(item => item.name === 'Fleet Management');
      if (fleetManagementItem && 'submenu' in fleetManagementItem && fleetManagementItem.submenu) {
        const submenu = fleetManagementItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Fleet Management', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in a safety submenu route
    if (path.startsWith('/safety/')) {
      const safetyItem = navigation.find(item => item.name === 'Safety');
      if (safetyItem && 'submenu' in safetyItem && safetyItem.submenu) {
        const submenu = safetyItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Safety', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in an analytics submenu route
    if (path.startsWith('/analytics/')) {
      const analyticsItem = navigation.find(item => item.name === 'Analytics');
      if (analyticsItem && 'submenu' in analyticsItem && analyticsItem.submenu) {
        const submenu = analyticsItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Analytics', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in an administration submenu route
    if (path.startsWith('/administration/')) {
      const administrationItem = navigation.find(item => item.name === 'Administration');
      if (administrationItem && 'submenu' in administrationItem && administrationItem.submenu) {
        const submenu = administrationItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Administration', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in a tracing submenu route
    if (path.startsWith('/tracing/')) {
      const tracingItem = navigation.find(item => item.name === 'Tracing');
      if (tracingItem && 'submenu' in tracingItem && tracingItem.submenu) {
        const submenu = tracingItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Tracing', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in a compliance portal submenu route
    if (path.startsWith('/compliance/')) {
      const complianceItem = navigation.find(item => item.name === 'Compliance Portal');
      if (complianceItem && 'submenu' in complianceItem && complianceItem.submenu) {
        const submenu = complianceItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Compliance Portal', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in a dispatching submenu route
    if (path.startsWith('/dispatching/')) {
      const dispatchingItem = navigation.find(item => item.name === 'Dispatching');
      if (dispatchingItem && 'submenu' in dispatchingItem && dispatchingItem.submenu) {
        const submenu = dispatchingItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Dispatching', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in a scheduling submenu route
    if (path.startsWith('/scheduling/')) {
      const schedulingItem = navigation.find(item => item.name === 'Scheduling');
      if (schedulingItem && 'submenu' in schedulingItem && schedulingItem.submenu) {
        const submenu = schedulingItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Scheduling', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in an order-management submenu route. /quotes/:id falls
    // back to the parent Quotes crumb so the breadcrumb stays
    // Home > Order Management > Quotes even on the detail page.
    if (path.startsWith('/order-management/')) {
      const orderManagementItem = navigation.find(item => item.name === 'Order Management');
      if (orderManagementItem && 'submenu' in orderManagementItem && orderManagementItem.submenu) {
        const submenu = orderManagementItem.submenu as any[];
        const subItem =
          submenu.find((sub: any) => sub.href === path) ||
          submenu.find((sub: any) => sub.href && path.startsWith(sub.href + '/')) ||
          submenu.find((sub: any) => sub.href && path.startsWith(sub.href));
        if (subItem) {
          return [
            { name: 'Order Management', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Accounting submenu route -> Home > Accounting > <page>.
    if (path.startsWith('/accounting/')) {
      const accountingItem = navigation.find(item => item.name === 'Accounting');
      if (accountingItem && 'submenu' in accountingItem && accountingItem.submenu) {
        const subItem = (accountingItem.submenu as any[]).find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Accounting', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're on a Data Management table route — the per-table pages now
    // live under the hub, so resolve the crumb from the registry.
    if (path.startsWith('/data-management/')) {
      const table = findTableByRoute(path);
      if (table) {
        return [
          { name: 'Table Maintenance', href: '/data-management' },
          { name: table.label, href: table.route }
        ];
      }
    }

    // Check if we're in a development submenu route
    if (path.startsWith('/development/')) {
      const developmentItem = navigation.find(item => item.name === 'Development');
      if (developmentItem && 'submenu' in developmentItem && developmentItem.submenu) {
        const submenu = developmentItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Development', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in a logging submenu route
    if (path.startsWith('/logging/')) {
      const loggingItem = navigation.find(item => item.name === 'Logging');
      if (loggingItem && 'submenu' in loggingItem && loggingItem.submenu) {
        const submenu = loggingItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Logging', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in a geofencing submenu route
    if (path.startsWith('/geofencing/')) {
      const geofencingItem = navigation.find(item => item.name === 'Geofencing');
      if (geofencingItem && 'submenu' in geofencingItem && geofencingItem.submenu) {
        const submenu = geofencingItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Geofencing', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in a yard management submenu route
    if (path.startsWith('/yard/')) {
      const yardManagementItem = navigation.find(item => item.name === 'Yard Management');
      if (yardManagementItem && 'submenu' in yardManagementItem && yardManagementItem.submenu) {
        const submenu = yardManagementItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Yard Management', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // Check if we're in a researching/reports submenu route
    if (path.startsWith('/researching-reports/')) {
      const researchingReportsItem = navigation.find(item => item.name === 'Researching / Reports');
      if (researchingReportsItem && 'submenu' in researchingReportsItem && researchingReportsItem.submenu) {
        const submenu = researchingReportsItem.submenu as any[];
        const subItem = submenu.find((sub: any) => sub.href === path);
        if (subItem) {
          return [
            { name: 'Researching / Reports', href: null },
            { name: subItem.name, href: subItem.href }
          ];
        }
      }
    }

    // For main routes
    const mainItem = navigation.find(item => item.href === path);
    if (mainItem) {
      return [{ name: mainItem.name, href: mainItem.href }];
    }

    return [];
  }, [location.pathname]);

  const handleNavClick = (item: any) => {
    // When the sidebar is showing as collapsed, don't toggle expansion on click for
    // expandable items — hover handles the popout instead. In Full-Slide mode the nav
    // is visually expanded while hovered, so `effectiveCollapsed` is false there and
    // clicking a parent expands its submenu inline as it would when pinned open.
    if (item.expandable && !effectiveCollapsed) {
      if (item.name === 'Fleet Management') {
        setFleetManagementExpanded(!fleetManagementExpanded);
      } else if (item.name === 'Safety') {
        setSafetyExpanded(!safetyExpanded);
      } else if (item.name === 'Analytics') {
        setAnalyticsExpanded(!analyticsExpanded);
      } else if (item.name === 'Dispatching') {
        setDispatchingExpanded(!dispatchingExpanded);
      } else if (item.name === 'Scheduling') {
        setSchedulingExpanded(!schedulingExpanded);
      } else if (item.name === 'Order Management') {
        setOrderManagementExpanded(!orderManagementExpanded);
      } else if (item.name === 'Accounting') {
        setAccountingExpanded(!accountingExpanded);
      } else if (item.name === 'Development') {
        setDevelopmentExpanded(!developmentExpanded);
      } else if (item.name === 'Logging') {
        setLoggingExpanded(!loggingExpanded);
      } else if (item.name === 'Geofencing') {
        setGeofencingExpanded(!geofencingExpanded);
      } else if (item.name === 'Yard Management') {
        setYardManagementExpanded(!yardManagementExpanded);
      } else if (item.name === 'Researching / Reports') {
        setResearchingReportsExpanded(!researchingReportsExpanded);
      } else if (item.name === 'CST') {
        setCstExpanded(!cstExpanded);
      } else if (item.name === 'Training') {
        setTrainingExpanded(!trainingExpanded);
      } else if (item.name === 'Administration') {
        setAdministrationExpanded(!administrationExpanded);
      } else if (item.name === 'Tracing') {
        setTracingExpanded(!tracingExpanded);
      } else if (item.name === 'Compliance Portal') {
        setCompliancePortalExpanded(!compliancePortalExpanded);
      } else if (item.href) {
        // Transiently-expandable hub (Data Management during nav search): it has
        // no expand state, so clicking the parent just opens the hub page.
        collapseAfterNavigate();
        navigate(item.href);
        setMobileMenuOpen(false);
      }
    } else if (item.href) {
      // Collapse before navigating so the slide-back overlaps the route transition.
      collapseAfterNavigate();
      navigate(item.href);
      setMobileMenuOpen(false); // Close mobile menu after navigation
    }
  };

  return (
    <>
      {/* Environment Banner - positioned absolutely above grid */}
      <EnvironmentBanner environment={environment} productionReadonlyMode={productionReadonlyMode} />

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Main Grid Container */}
      <div
        className={`${styles.container} ${darkMode ? styles.dark : styles.light} ${sidebarCollapsed ? styles.containerCollapsed : ''}`}
      >
        {/* Nav (Sidebar) */}
        <div
          data-app-nav="true"
          className={`${styles.nav} ${effectiveCollapsed ? styles.navCollapsed : ''} ${mobileMenuOpen ? styles.navMobileOpen : ''} ${isTestEnvironment ? styles.testEnvironment : ''} ${showGridBorders ? styles.navBorder : ''} ${showGridHighlight ? styles.navHighlight : ''} ${showGridLabels ? styles.navLabel : ''} ${fullSlideEnabled ? styles.navFullSlideCapable : ''} ${slidOut ? styles.navFullSlideOut : ''}`}
          onMouseEnter={navHandlers.onMouseEnter}
          onMouseLeave={navHandlers.onMouseLeave}
          onFocus={navHandlers.onFocus}
          onBlur={navHandlers.onBlur}
        >
        <GridLabel areaName="nav" position="top-left" />
        <div className={styles.sidebarContent}>
          {/* Logo Header */}
          <div className={styles.sidebarHeader}>
            <h1 className={styles.logo}>
              {effectiveCollapsed ? 'gwi' : (isTestEnvironment ? 'gwi360 - Test' : 'gwi360')}
            </h1>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className={`${styles.closeButton} ${styles.mobileOnly}`}
              aria-label="Close menu"
            >
              <XMarkIcon className={styles.closeIcon} />
            </button>
          </div>

          {/* Navigation Search */}
          {(!effectiveCollapsed || mobileMenuOpen) && (
            <div className={styles.navSearchWrapper}>
              <MagnifyingGlassIcon className={styles.navSearchIcon} />
              <input
                type="text"
                placeholder={navEditMode ? 'Reordering…' : 'Filter Navigation'}
                value={navSearchQuery}
                onChange={(e) => setNavSearchQuery(e.target.value)}
                className={styles.navSearchInput}
                disabled={navEditMode}
              />
              {navSearchQuery && !navEditMode && (
                <button
                  onClick={() => setNavSearchQuery('')}
                  className={styles.navSearchClear}
                  aria-label="Clear search"
                >
                  <XMarkIcon className={styles.navSearchClearIcon} />
                </button>
              )}
            </div>
          )}

          {/* Collapsed-rail nav filter: magnifier above Home, flyout on hover/click */}
          {showCollapsedSearch && (
            <div
              className={styles.collapsedSearchWrapper}
              onMouseEnter={() => setCollapsedSearchHovered(true)}
              onMouseLeave={() => setCollapsedSearchHovered(false)}
            >
              <button
                type="button"
                onClick={() => setCollapsedSearchPinned((v) => !v)}
                className={`${styles.collapsedSearchButton} ${navSearchQuery ? styles.collapsedSearchButtonActive : ''}`}
                aria-label={navSearchQuery ? `Filter navigation (filtering by "${navSearchQuery}")` : 'Filter navigation'}
                aria-expanded={collapsedSearchOpen}
              >
                <MagnifyingGlassIcon className={styles.navIcon} />
                {/* A filtered rail just looks like a short rail — this dot says why */}
                {navSearchQuery && <span className={styles.collapsedSearchDot} />}
              </button>

              {collapsedSearchOpen && (
                <div className={styles.collapsedSearchPopout}>
                  <div className={styles.collapsedSearchFlyout}>
                    <MagnifyingGlassIcon className={styles.navSearchIcon} />
                    <input
                      ref={collapsedSearchInputRef}
                      type="text"
                      placeholder="Filter Navigation"
                      value={navSearchQuery}
                      onChange={(e) => setNavSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Escape') return;
                        // Escape clears a live filter first, then dismisses the flyout —
                        // so it never strands you with a filter you can't see the cause of.
                        if (navSearchQuery) {
                          setNavSearchQuery('');
                        } else {
                          setCollapsedSearchPinned(false);
                          setCollapsedSearchHovered(false);
                        }
                      }}
                      className={styles.navSearchInput}
                    />
                    {navSearchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setNavSearchQuery('');
                          collapsedSearchInputRef.current?.focus();
                        }}
                        className={styles.navSearchClear}
                        aria-label="Clear navigation filter"
                      >
                        <XMarkIcon className={styles.navSearchClearIcon} />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <nav className={styles.navScrollable}>
            {navEditMode && permissions ? (
              <NavigationOrderEditor
                disabled={navOrderLoading || navOrderSaving}
                groups={[
                  { id: 'main-nav', name: 'Navigation sections', items: orderedNavigation.map(item => ({ id: item.name, name: item.name })) },
                  { id: 'favorites', name: 'Favorites', items: orderedFavorites.filter(fav =>
                    resolveFavoritable(navigation, fav.href) &&
                    (!fav.href.startsWith('/data-management/') || hasAccess(fav.href.split(/[?#]/)[0]))
                  ).map(fav => ({ id: fav.href, name: fav.name })) },
                  ...orderedNavigation.filter(item => item.submenu).map(item => ({
                    id: item.name, name: item.name,
                    items: item.submenu.map((sub: { name: string }) => ({ id: sub.name, name: sub.name })),
                  })),
                ]}
                onReorder={(group, ids) => group === 'main-nav' ? saveNavOrder(ids) : saveNavGroupOrder(group, ids)}
              />
            ) : (
            <ul className={styles.navList}>
              {!permissions ? (
                // Loading skeleton while permissions are being fetched
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <li key={i} className={styles.navItem}>
                      <div className={`${styles.navLink} animate-pulse`}>
                        <div className="w-6 h-6 bg-gray-700 rounded"></div>
                        {!effectiveCollapsed && <div className="ml-3 h-4 bg-gray-700 rounded w-24"></div>}
                      </div>
                    </li>
                  ))}
                </>
              ) : (
                searchFilteredNavigation.map((item: any) => {
                const isActive = location.pathname === item.href;
                const isSubmenuActive = item.submenu?.some((sub: any) => location.pathname === sub.href);

                return (
                  <React.Fragment key={item.name}>
                    <li
                      onMouseEnter={(e) => {
                        // Full-Slide replaces the per-icon popouts outright — gate on
                        // navFullSlide (not just effectiveCollapsed) so no popout can
                        // flash during the slide-out delay.
                        if (effectiveCollapsed && !navFullSlide && item.expandable) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setPopoutTop(rect.top - 50); // Move up by 50px
                          setHoveredItem(item.name);
                        }
                      }}
                      onMouseLeave={() => {
                        if (effectiveCollapsed && !navFullSlide && item.expandable) {
                          setHoveredItem(null);
                        }
                      }}
                      className={styles.navItem}
                    >
                      {item.expandable ? (
                        effectiveCollapsed && !mobileMenuOpen ? (
                          <button
                            onClick={() => handleNavClick(item)}
                            className={`${styles.navLink} ${isSubmenuActive ? styles.navLinkActive : ''} ${styles.navButton}`}
                          >
                            <item.icon className={styles.navIcon} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleNavClick(item)}
                            className={`${styles.navLink} ${isSubmenuActive ? styles.navLinkActive : ''} ${styles.navButton}`}
                          >
                            <item.icon className={styles.navIcon} />
                            <>
                              <span className={styles.navText}>{item.name}</span>
                              <ChevronDownIcon className={`${styles.chevronIcon} ${item.expanded ? styles.chevronExpanded : ''}`} />
                            </>
                          </button>
                        )
                      ) : (
                        effectiveCollapsed && !mobileMenuOpen ? (
                          <Tooltip content={item.name} placement="right" delay={100}>
                            <a
                              href={item.href || '#'}
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavClick(item);
                              }}
                              className={`${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}
                            >
                              <item.icon className={styles.navIcon} />
                            </a>
                          </Tooltip>
                        ) : (
                          <a
                            href={item.href || '#'}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick(item);
                            }}
                            className={`${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}
                          >
                            <item.icon className={styles.navIcon} />
                            <span className={styles.navText}>{item.name}</span>
                          </a>
                        )
                      )}
                    </li>

                    {/* Submenu items - show differently based on sidebar state */}
                    {item.expandable && item.submenu && (
                      <>
                        {/* Expanded mode submenu (also used by Full-Slide, which renders
                            the nav as expanded while hovered) */}
                        {item.expanded && !effectiveCollapsed && (
                          <li>
                            <ul className={styles.submenu}>
                              {item.submenu.map((subItem: any) => {
                                const isSubActive = location.pathname === subItem.href;
                                return (
                                  <li key={subItem.name}>
                                    <a
                                      href={subItem.href}
                                      target={subItem.external ? '_blank' : undefined}
                                      rel={subItem.external ? 'noopener noreferrer' : undefined}
                                      onClick={(e) => {
                                        if (subItem.external) return; // let the browser open the real link
                                        e.preventDefault();
                                        handleNavClick(subItem);
                                      }}
                                      className={`${styles.submenuLink} ${isSubActive ? styles.submenuLinkActive : ''}`}
                                    >
                                      <subItem.icon className={styles.submenuIcon} />
                                      <span className={styles.submenuText}>{subItem.name}</span>
                                      {renderNavBadge(subItem.href)}
                                    </a>
                                  </li>
                                );
                              })}
                            </ul>
                          </li>
                        )}

                        {/* Collapsed mode hover popout — suppressed entirely in
                            Full-Slide mode, which expands submenus inline instead */}
                        {effectiveCollapsed && !navFullSlide && !mobileMenuOpen && hoveredItem === item.name && item.submenu && (
                          <div
                            className={styles.submenuPopout}
                            style={{ top: `${popoutTop}px` }}
                            onMouseEnter={() => setHoveredItem(item.name)}
                            onMouseLeave={() => setHoveredItem(null)}
                          >
                            <div className={styles.submenuFlyout}>
                              <ul className={styles.flyoutList}>
                                {item.submenu.map((subItem: any) => {
                                  const isSubActive = location.pathname === subItem.href;
                                  return (
                                    <li key={subItem.name}>
                                      <a
                                        href={subItem.href}
                                        target={subItem.external ? '_blank' : undefined}
                                        rel={subItem.external ? 'noopener noreferrer' : undefined}
                                        onClick={(e) => {
                                          if (subItem.external) { setHoveredItem(null); return; }
                                          e.preventDefault();
                                          handleNavClick(subItem);
                                          setHoveredItem(null);
                                        }}
                                        className={`${styles.flyoutLink} ${isSubActive ? styles.flyoutLinkActive : ''}`}
                                      >
                                        <subItem.icon className={styles.flyoutIcon} />
                                        <span className={styles.flyoutText}>{subItem.name}</span>
                                        {/* The collapsed rail is an icon-only strip with no room
                                            for a badge, so on a collapsed sidebar the count rides
                                            the hover flyout instead — it degrades to "visible when
                                            you open Fleet Management" rather than breaking the rail. */}
                                        {renderNavBadge(subItem.href)}
                                      </a>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {item.name === 'Home' && favorites.length > 0 && !effectiveCollapsed && (
                      <>
                        <li className={styles.navItem}>
                          <button
                            onClick={() => setFavoritesExpanded(!favoritesExpanded)}
                            className={`${styles.navLink} ${styles.navButton}`}
                            aria-expanded={favoritesExpanded}
                            aria-controls="favorites-submenu"
                          >
                            <StarIcon className={styles.navIcon} />
                            <span className={styles.navText}>Favorites</span>
                            <ChevronDownIcon className={`${styles.chevronIcon} ${favoritesExpanded ? styles.chevronExpanded : ''}`} />
                          </button>
                        </li>
                        {favoritesExpanded && (
                          <li>
                            <ul id="favorites-submenu" className={styles.submenu}>
                              {orderedFavorites.map((fav) => {
                                const leaf = resolveFavoritable(navigation, fav.href);
                                if (!leaf) return null;
                                // A table favorite is gated like the sidebar search: lose the
                                // permission and it drops out rather than 403ing on click.
                                if (fav.href.startsWith('/data-management/') && !hasAccess(fav.href.split(/[?#]/)[0])) return null;
                                const SubIcon = leaf.icon;
                                const isSubActive = currentFavoriteHref === fav.href || location.pathname === fav.href;
                                return (
                                  <li key={fav.href}>
                                    <a
                                      href={fav.href}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleNavClick({ href: fav.href, name: fav.name });
                                      }}
                                      className={`${styles.submenuLink} ${isSubActive ? styles.submenuLinkActive : ''}`}
                                    >
                                      <SubIcon className={styles.submenuIcon} />
                                      <span className={styles.submenuText}>{fav.name}</span>
                                    </a>
                                  </li>
                                );
                              })}
                            </ul>
                          </li>
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })
              )}
            </ul>
            )}
          </nav>

          {/* Sidebar Footer */}
          <div className={styles.sidebarFooter}>
            {/* Navigation Reorder toggle (pinned above user block) */}
            {(!effectiveCollapsed || mobileMenuOpen) && (
              <>
                <div className={styles.navReorderWrapper}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!navEditMode && sidebarCollapsed) setSidebarCollapsed(false);
                      if (navEditMode && navSearchQuery) setNavSearchQuery('');
                      setNavEditMode((v) => !v);
                    }}
                    className={styles.navReorderButton}
                    aria-label={navEditMode ? 'Done editing navigation' : 'Edit navigation order'}
                    aria-pressed={navEditMode}
                  >
                    {navEditMode ? <CheckIcon className="w-5 h-5" /> : <PencilSquareIcon className="w-5 h-5" />}
                    <span>{navEditMode ? 'Done' : 'Reorder Navigation'}</span>
                  </button>
                </div>
                <div className={styles.footerDivider}></div>
              </>
            )}

            {/* User Profile Section — settings gear sits beside the name when expanded */}
            <div className={styles.userRow}>
              <div className={styles.userProfile}>
                <CommunicationProfileLink onClick={() => setMobileMenuOpen(false)}>
                {userPhotoUrl ? (
                  <img
                    src={userPhotoUrl}
                    alt={currentUser?.name || 'User'}
                    className={styles.userAvatar}
                    style={{ objectFit: 'cover' }}
                  />
                ) : (
                  <div className={styles.userAvatar}>
                    {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
                </CommunicationProfileLink>
                {!effectiveCollapsed && (
                  <button type="button" className={`${styles.userInfo} text-left`} onClick={() => setUserDrawerOpen(true)} aria-label="Open user profile">
                    <div className={styles.userName}>{currentUser?.name || 'User'}</div>
                    <div className={styles.userCompany}>
                      {userProfile?.jobTitle || userProfile?.department || userProfile?.companyName || 'Gulf Winds International'}
                    </div>
                  </button>
                )}
              </div>
              {!effectiveCollapsed && (
                <Tooltip content="Settings" placement="top">
                  <button
                    className={`${styles.footerActionButton} ${styles.userSettingsButton}`}
                    onClick={() => setSettingsModalOpen(true)}
                    aria-label="Settings"
                  >
                    <SettingsIcon className={styles.userSettingsIcon} />
                  </button>
                </Tooltip>
              )}
            </div>

            {/* Footer Divider */}
            <div className={styles.footerDivider}></div>

            {/* Footer Actions with Collapse Button */}
            <div className={styles.footerActionsRow}>
              {effectiveCollapsed ? (
                <div className={styles.footerCollapsedLayout}>
                  {/* Help icon hidden for now
                  <Tooltip content="Help" placement="right">
                    <button
                      className={styles.footerActionButton}
                      onClick={() => setHelpModalOpen(true)}
                    >
                      <QuestionMarkCircleIcon className={styles.footerIcon} />
                    </button>
                  </Tooltip>
                  */}
                  <Tooltip content="Settings" placement="right">
                    <button
                      className={styles.footerActionButton}
                      onClick={() => setSettingsModalOpen(true)}
                    >
                      <SettingsIcon className={styles.footerIcon} />
                    </button>
                  </Tooltip>
                  <Tooltip content="Helpdesk Ticket" placement="right">
                    <button
                      className={styles.footerActionButton}
                      onClick={() => setHelpdeskTicketDrawerOpen(true)}
                    >
                      <LifeRingIcon className={styles.footerIcon} />
                    </button>
                  </Tooltip>
                  <Tooltip content="Report a Bug" placement="right">
                    <button
                      className={styles.footerActionButton}
                      onClick={() => setBugReportDrawerOpen(true)}
                    >
                      <BugReportIcon className={styles.footerIcon} />
                    </button>
                  </Tooltip>
                  <Tooltip content="Request a Feature" placement="right">
                    <button
                      className={styles.footerActionButton}
                      onClick={() => setFeatureRequestDrawerOpen(true)}
                    >
                      <FeaturesIcon className={styles.footerIcon} />
                    </button>
                  </Tooltip>
                  <Tooltip content="My Tickets" placement="right">
                    <button
                      className={styles.footerActionButton}
                      onClick={() => navigate('/my-tickets')}
                    >
                      <ReceiptIcon className={styles.footerIcon} />
                    </button>
                  </Tooltip>
                  <Tooltip content="Logout" placement="right">
                    <button
                      onClick={handleLogout}
                      className={styles.footerActionButton}
                    >
                      <ArrowRightOnRectangleIcon className={styles.footerIcon} />
                    </button>
                  </Tooltip>
                  <button
                    onClick={toggleSidebar}
                    className={styles.toggleButton}
                    aria-label="Toggle sidebar"
                  >
                    <ChevronRightIcon className={styles.toggleIcon} />
                  </button>
                </div>
              ) : (
                <>
                  <div className={styles.footerLeftIcons}>
                    <Tooltip content="Logout" placement="top">
                      <button
                        onClick={handleLogout}
                        className={styles.footerActionButton}
                      >
                        <ArrowRightOnRectangleIcon className={styles.footerIcon} />
                      </button>
                    </Tooltip>
                    {/* Help icon hidden for now
                    <Tooltip content="Help" placement="top">
                      <button
                        className={styles.footerActionButton}
                        onClick={() => setHelpModalOpen(true)}
                      >
                        <QuestionMarkCircleIcon className={styles.footerIcon} />
                      </button>
                    </Tooltip>
                    */}
                    <Tooltip content="Helpdesk Ticket" placement="top">
                      <button
                        className={styles.footerActionButton}
                        onClick={() => setHelpdeskTicketDrawerOpen(true)}
                      >
                        <LifeRingIcon className={styles.footerIcon} />
                      </button>
                    </Tooltip>
                    <Tooltip content="Report a Bug" placement="top">
                      <button
                        className={styles.footerActionButton}
                        onClick={() => setBugReportDrawerOpen(true)}
                      >
                        <BugReportIcon className={styles.footerIcon} />
                      </button>
                    </Tooltip>
                    <Tooltip content="Request a Feature" placement="top">
                      <button
                        className={styles.footerActionButton}
                        onClick={() => setFeatureRequestDrawerOpen(true)}
                      >
                        <FeaturesIcon className={styles.footerIcon} />
                      </button>
                    </Tooltip>
                    <Tooltip content="My Tickets" placement="top">
                      <button
                        className={styles.footerActionButton}
                        onClick={() => navigate('/my-tickets')}
                      >
                        <ReceiptIcon className={styles.footerIcon} />
                      </button>
                    </Tooltip>
                  </div>
                  {/* While Full-Slide has the nav temporarily out, this button's job is
                      "pin it open", not "collapse it" — so it shows a pushpin. Clicking
                      it clears sidebarCollapsed, which also disables Full-Slide until
                      the user collapses the nav again. */}
                  {slidOut ? (
                    <Tooltip content="Pin menu open" placement="top">
                      <button
                        onClick={() => setSidebarCollapsed(false)}
                        className={styles.toggleButton}
                        aria-label="Pin menu open"
                      >
                        <PinIcon className={styles.toggleIcon} />
                      </button>
                    </Tooltip>
                  ) : (
                    <button
                      onClick={toggleSidebar}
                      className={styles.toggleButton}
                      aria-label="Toggle sidebar"
                    >
                      <ChevronLeftIcon className={styles.toggleIcon} />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

        </div>
        </div>

        {/* Breadcrumb */}
        <div className={`${styles.breadcrumb} ${showGridBorders ? styles.breadcrumbBorder : ''} ${showGridHighlight ? styles.breadcrumbHighlight : ''} ${showGridLabels ? styles.breadcrumbLabel : ''}`} style={{ position: 'relative' }}>
          <GridLabel areaName="breadcrumb" position="top-left" />
          {/* Hamburger Menu for Mobile */}
          <button
            className={styles.hamburgerButton}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Open menu"
          >
            <Bars3Icon className={styles.hamburgerIcon} />
          </button>

          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className={styles.breadcrumbs}>
            <ol className={styles.breadcrumbList}>
              <li className={styles.breadcrumbItem}>
                <div className={styles.breadcrumbItemContent}>
                  <a
                    href="/home"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/home');
                    }}
                    className={styles.breadcrumbHomeLink}
                  >
                    <HomeIcon aria-hidden="true" className={styles.breadcrumbHomeIcon} />
                    <span className={styles.srOnly}>Home</span>
                  </a>
                </div>
              </li>
              {breadcrumbItems.map((item, index) => {
                const isLastItem = index === breadcrumbItems.length - 1;
                return (
                <li key={item.name} className={`${styles.breadcrumbItem} ${!isLastItem ? 'hidden lg:flex' : ''}`}>
                  <div className={styles.breadcrumbItemContent}>
                    <svg
                      fill="currentColor"
                      viewBox="0 0 24 44"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                      className={styles.breadcrumbSeparator}
                    >
                      <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                    </svg>
                    {item.href && isLastItem ? (
                      <span
                        aria-current="page"
                        className={styles.currentPageTitle}
                      >
                        {item.name}
                      </span>
                    ) : item.href ? (
                      <a
                        href={item.href}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(item.href as string);
                        }}
                        className={styles.breadcrumbText}
                        style={{ cursor: 'pointer' }}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <span className={styles.breadcrumbText}>
                        {item.name}
                      </span>
                    )}
                  </div>
                </li>
                );
              })}
            </ol>
          </nav>

          {/* Global Search */}
          <div className="flex-1 max-w-md mx-4 hidden md:block">
            <GlobalSearch />
          </div>

          <div className={styles.topBarActions}>
            <MarketConditionsPill
              open={marketConditionsOpen}
              onOpenChange={(open) => { setMarketConditionsOpen(open); if (!open) setMarketConditionsFocus(null); }}
              focusMarket={marketConditionsFocus}
              onManage={() => setMarketConditionsDrawerOpen(true)}
            />
            {hasAccess('/data-management/fuel-index') && (
              <DriverRateCalculator className={styles.themeToggle} iconClassName={styles.themeIcon} />
            )}
            <button
              onClick={toggleTheme}
              className={styles.themeToggle}
              aria-label="Toggle theme"
            >
              {darkMode ? (
                <SunIcon className={styles.themeIcon} />
              ) : (
                <MoonIcon className={styles.themeIcon} />
              )}
            </button>

            {/* Favorite toggle */}
            <Tooltip
              content={
                !canFavoriteCurrent
                  ? "This page can't be favorited"
                  : currentIsFavorited
                  ? "Remove from favorites"
                  : "Add to favorites"
              }
              placement="bottom-end"
            >
              <button
                onClick={() => {
                  if (!currentFavorite) return;
                  void toggleFavorite(currentFavorite.name, favoriteToggleHref);
                }}
                aria-disabled={!canFavoriteCurrent}
                className={styles.themeToggle}
                aria-label={
                  currentIsFavorited ? "Remove from favorites" : "Add to favorites"
                }
                aria-pressed={canFavoriteCurrent ? currentIsFavorited : undefined}
              >
                {currentIsFavorited ? (
                  <StarSolidIcon className={`${styles.themeIcon} text-yellow-400`} />
                ) : (
                  <StarIcon className={styles.themeIcon} />
                )}
              </button>
            </Tooltip>

            {/* My History Button - Always visible */}
            <Tooltip content="My History" placement="bottom-end">
              <button
                onClick={() => setUserHistoryModalOpen(true)}
                className={styles.themeToggle}
                aria-label="View My History"
              >
                <UserTimeIcon className={styles.themeIcon} />
              </button>
            </Tooltip>

            {/* Page Info Button - Always visible */}
            <Tooltip content="Page Info" placement="bottom-end">
              <button
                onClick={openLegend}
                className={styles.themeToggle}
                aria-label="View page info"
              >
                <InformationCircleIcon className={`${styles.themeIcon} ${legendSections.length > 0 ? 'text-green-600 dark:text-green-400' : ''}`} />
              </button>
            </Tooltip>

            {/* Permissions Mode Icon - Only visible to Developer group */}
            {isInDeveloperGroup && (
              <Tooltip content={permissionsMode ? "Exit Permissions Mode" : "Configure Permissions"} placement="bottom-end">
                <button
                  onClick={() => {
                    setPermissionsMode(!permissionsMode);
                  }}
                  className={`${styles.themeToggle} ${permissionsMode ? 'ring-2 ring-purple-500 ring-offset-2' : ''}`}
                  aria-label="Configure permissions"
                >
                  <UserPermissionsIcon className={`${styles.themeIcon} ${permissionsMode ? 'text-purple-600 dark:text-purple-400' : ''}`} />
                </button>
              </Tooltip>
            )}

            {/* Impersonate User Icon - Only visible to Developer group */}
            {isInDeveloperGroup && (
              <Tooltip content="Impersonate User" placement="bottom-end">
                <button
                  onClick={() => setImpersonationModalOpen(true)}
                  className={styles.themeToggle}
                  aria-label="Impersonate user"
                >
                  <UserIcon className={styles.themeIcon} />
                </button>
              </Tooltip>
            )}

          </div>
        </div>

        {/* Body - Pages define their own grid layout here */}
        <div className={`${styles.body} ${showGridBorders ? styles.bodyBorder : ''} ${showGridHighlight ? styles.bodyHighlight : ''} ${showGridLabels ? styles.bodyLabel : ''}`}>
          {/* Permissions Mode Toolbar */}
          <PermissionsModeToolbar />

          {/* Impersonation Banner */}
          <ImpersonationBanner />

          {/* Impersonation Modal - Only mounted for Developer group */}
          {isInDeveloperGroup && (
            <UserImpersonationModal
              isOpen={impersonationModalOpen}
              onClose={() => setImpersonationModalOpen(false)}
            />
          )}

          {/* My History Modal - Available to all users; admins get the user picker */}
          <UserHistoryModal
            isOpen={userHistoryModalOpen}
            onClose={() => setUserHistoryModalOpen(false)}
            isAdmin={isInDeveloperGroup}
          />


          {/* Polls for the current user's bugs/features that just resolved in Zendesk */}
          <ClosedTicketToast />

          {/* Market Conditions editor, reachable from the header pill on any page */}
          <MarketConditionsDrawer isOpen={marketConditionsDrawerOpen} onClose={() => setMarketConditionsDrawerOpen(false)} />

          <FavoriteTabContext.Provider value={favoriteTabContext}>{children}</FavoriteTabContext.Provider>
        </div>

        {/* Footer */}
        {location.pathname !== '/development/component-library' && (
          <div className={`${styles.footer} ${showGridBorders ? styles.footerBorder : ''} ${showGridHighlight ? styles.footerHighlight : ''} ${showGridLabels ? styles.footerLabel : ''}`} style={{ position: 'relative' }}>
            <GridLabel areaName="footer" position="top-left" />
            {/* Footer grid area is column 2 — the Full-Slide overlay never moves it,
                so this keeps the persisted pin, not `effectiveCollapsed`. */}
            <SyncStatusFooter sidebarCollapsed={sidebarCollapsed} isTestEnvironment={isTestEnvironment} />
          </div>
        )}
      </div>

      {/* User Details Drawer */}
      {currentUser && (
        <Drawer
          isOpen={userDrawerOpen}
          onClose={() => setUserDrawerOpen(false)}
          title="User Profile"
          position="left"
          maxWidth="md"
        >
          <div className="space-y-6">
            {/* User Avatar and Name */}
            <div className="flex items-center space-x-4">
              {userPhotoUrl ? (
                <img
                  src={userPhotoUrl}
                  alt={currentUser.name || 'User'}
                  className="size-20 rounded-full object-cover shadow-lg ring-2 ring-indigo-500 ring-offset-2"
                />
              ) : (
                <div className="size-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
              <div>
                <h3 className="text-xl font-semibold text-heading">
                  {currentUser.name || 'User'}
                </h3>
                <p className="text-sm text-muted">
                  {currentUser.username || currentUser.localAccountId}
                </p>
              </div>
            </div>

            {/* User Details Section */}
            <div className="border-t border-theme-border-primary pt-6">
              <h4 className="text-sm font-medium text-heading mb-4">
                Account Information
              </h4>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-muted">
                    Name
                  </dt>
                  <dd className="mt-1 text-sm text-heading">
                    {currentUser.name || 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted">
                    Email
                  </dt>
                  <dd className="mt-1 text-sm text-heading">
                    {currentUser.username || 'N/A'}
                  </dd>
                </div>
                {userProfile?.jobTitle && (
                  <div>
                    <dt className="text-sm font-medium text-muted">
                      Job Title
                    </dt>
                    <dd className="mt-1 text-sm text-heading">
                      {userProfile.jobTitle}
                    </dd>
                  </div>
                )}
                {userProfile?.department && (
                  <div>
                    <dt className="text-sm font-medium text-muted">
                      Department
                    </dt>
                    <dd className="mt-1 text-sm text-heading">
                      {userProfile.department}
                    </dd>
                  </div>
                )}
                {userProfile?.officeLocation && (
                  <div>
                    <dt className="text-sm font-medium text-muted">
                      Office Location
                    </dt>
                    <dd className="mt-1 text-sm text-heading">
                      {userProfile.officeLocation}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Active Groups Section */}
            {permissions && permissions.groups && permissions.groups.length > 0 && userEnabledGroups.length > 0 && (
              <div className="border-t border-theme-border-primary pt-6">
                <h4 className="text-sm font-medium text-heading mb-4">
                  Active Groups
                </h4>
                <div className="flex flex-wrap gap-2">
                  {userEnabledGroups
                    .filter((group: any) => permissions.groups.includes(group.group_id))
                    .map((group: any) => (
                      <span
                        key={group.id}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300"
                      >
                        {group.display_name || group.group_name}
                      </span>
                    ))}
                  {userEnabledGroups.filter((group: any) => permissions.groups.includes(group.group_id)).length === 0 && (
                    <p className="text-sm text-muted">No active groups</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </Drawer>
      )}

      {/* Bug Report Modal */}
      <DraggableModal
        isOpen={bugReportDrawerOpen}
        onClose={() => setBugReportDrawerOpen(false)}
        title="Report a Bug"
        width={640}
        forceHidden={bugReportDrawerTemporaryHidden}
        contentClassName="flex-1 overflow-y-auto"
        onPopOut={() => {
          const from = encodeURIComponent(window.location.href);
          openPopoutWindow(`/popout/bug-report?from=${from}`, 'fastcms-bug-report');
          setBugReportDrawerOpen(false);
        }}
      >
        <BugReportForm
          isOpen={bugReportDrawerOpen}
          onCancel={() => setBugReportDrawerOpen(false)}
          onTemporaryClose={() => setBugReportDrawerTemporaryHidden(true)}
          onTemporaryReopen={() => setBugReportDrawerTemporaryHidden(false)}
          onShowScreenshotModal={(screenshot, updateCallback) => {
            setScreenshotModalImage(screenshot);
            setScreenshotUpdateCallback(() => updateCallback);
            setScreenshotSource('bug');
            setScreenshotModalOpen(true);
            setBugReportDrawerTemporaryHidden(true);
          }}
        />
      </DraggableModal>

      {/* GWI360 Assistant */}
      <GWI360Assistant
        isOpen={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />

      {/* Settings Modal */}
      <Drawer
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        title="Settings"
        position="right"
        maxWidth="md"
      >
        <div className="space-y-2">
          {/* Theme Settings */}
          <details className="group border border-theme-border-primary rounded-lg">
            <summary className="flex items-center justify-between cursor-pointer p-4 hover:bg-theme-bg-tertiary rounded-lg">
              <div className="flex items-center gap-3">
                <SunIcon className="w-5 h-5 text-muted" />
                <span className="font-medium text-heading">Theme Settings</span>
              </div>
              <ChevronDownIcon className="w-5 h-5 text-muted transition-transform group-open:rotate-180" />
            </summary>
            <div className="p-4 border-t border-theme-border-primary space-y-6">
              {/* Dark Mode Palette — base surface colors used in dark mode.
                  Independent of the custom-tint toggle below. */}
              <div>
                <label className="text-sm font-medium text-heading">
                  Dark Mode Palette
                </label>
                <p className="text-xs text-muted mb-2">
                  Base surface colors used in dark mode
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { key: 'blue', name: 'Blue', desc: 'Classic tinted (default)', chips: ['#111827', '#1f2937', '#374151'] },
                    { key: 'grey', name: 'Grey', desc: 'Neutral (DevOps)', chips: ['#1b1b1b', '#262626', '#363636'] },
                  ] as const).map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setDarkPaletteVariant(opt.key)}
                      aria-pressed={darkPaletteVariant === opt.key}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        darkPaletteVariant === opt.key
                          ? 'border-brand-red ring-2 ring-brand-red/40'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex gap-1 mb-2">
                        {opt.chips.map((c) => (
                          <span
                            key={c}
                            className="h-6 flex-1 rounded border border-black/10"
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                      <div className="text-sm font-medium text-heading">{opt.name}</div>
                      <div className="text-xs text-muted">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Enable Theme Colors Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Enable Custom Theme Colors
                  </label>
                  <p className="text-xs text-muted">
                    Apply custom color tints to backgrounds
                  </p>
                </div>
                <Toggle
                  checked={themeEnabled}
                  onChange={(checked) => setThemeEnabled(checked)}
                  size="sm"
                />
              </div>

              {/* Pointer to the full editor. These two sliders only tint a handful
                  of surfaces; the Customize page drives the whole token system -
                  colors, typography, density, per-component overrides and presets -
                  and can publish a theme for everyone. Kept rather than removed
                  because this is still where people look for it. */}
              <div className="text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded-md">
                These sliders tint a few surfaces. For the full editor - colors,
                typography, spacing and saved presets - open{' '}
                <button
                  type="button"
                  onClick={() => { setSettingsModalOpen(false); navigate('/administration/customize'); }}
                  className="font-medium underline hover:no-underline"
                >
                  Customize
                </button>
                .
              </div>

              {/* Light Mode Theme */}
              {themeEnabled && (
                <>
                  <ThemeColorPicker
                    label="Light Mode Theme"
                    color={lightThemeColor}
                    onChange={setLightThemeColor}
                    isDark={false}
                  />

                  {/* Dark Mode Theme */}
                  <ThemeColorPicker
                    label="Dark Mode Theme"
                    color={darkThemeColor}
                    onChange={setDarkThemeColor}
                    isDark={true}
                  />
                </>
              )}
            </div>
          </details>

          {/* Notification Preferences */}
          <details className="group border border-theme-border-primary rounded-lg">
            <summary className="flex items-center justify-between cursor-pointer p-4 hover:bg-theme-bg-tertiary rounded-lg">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                </svg>
                <span className="font-medium text-heading">Notification Preferences</span>
              </div>
              <ChevronDownIcon className="w-5 h-5 text-muted transition-transform group-open:rotate-180" />
            </summary>
            <div className="p-4 border-t border-theme-border-primary space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Show Toast For Driver Requesting Work
                  </label>
                  <p className="text-xs text-muted">
                    Display a toast on the Driver Status Monitor when a driver requests more work
                  </p>
                </div>
                <Toggle
                  checked={showDriverRequestToast}
                  onChange={(checked) => setShowDriverRequestToast(checked)}
                  size="sm"
                />
              </div>
              <div className={`flex items-center justify-between pl-6 border-l-2 border-theme-border-primary ${showDriverRequestToast ? '' : 'opacity-50'}`}>
                <div>
                  <label className="text-sm font-medium text-heading">
                    Show Only My Drivers
                  </label>
                  <p className="text-xs text-muted">
                    Only show toasts for drivers whose dispatcher is you
                  </p>
                </div>
                <Toggle
                  checked={showOnlyMyDrivers}
                  onChange={(checked) => setShowOnlyMyDrivers(checked)}
                  size="sm"
                  disabled={!showDriverRequestToast}
                />
              </div>
              {/* A SIBLING of the driver rows, not a child of them: unrelated
                  notification, so no indent. Renders only for the maintenance
                  audience that actually receives these toasts. */}
              <RepairOrderToastPreferenceRow
                checked={showRepairOrderToast}
                onChange={setShowRepairOrderToast}
              />
            </div>
          </details>

          {/* Display Preferences */}
          <details className="group border border-theme-border-primary rounded-lg">
            <summary className="flex items-center justify-between cursor-pointer p-4 hover:bg-theme-bg-tertiary rounded-lg">
              <div className="flex items-center gap-3">
                <Squares2X2Icon className="w-5 h-5 text-muted" />
                <span className="font-medium text-heading">Display Preferences</span>
              </div>
              <ChevronDownIcon className="w-5 h-5 text-muted transition-transform group-open:rotate-180" />
            </summary>
            <div className="p-4 border-t border-theme-border-primary space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Show Gridlines on Tables
                  </label>
                  <p className="text-xs text-muted">
                    Display grid borders between table cells
                  </p>
                </div>
                <Toggle
                  checked={showTableGridlines}
                  onChange={(checked) => setShowTableGridlines(checked)}
                  size="sm"
                />
              </div>
              {showTableGridlines && (
                <div className="ml-4 pl-4 border-l-2 border-theme-border-secondary space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-label">
                      Show Horizontal
                    </label>
                    <Toggle
                      checked={showHorizontalGridlines}
                      onChange={(checked) => setShowHorizontalGridlines(checked)}
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-label">
                      Show Vertical
                    </label>
                    <Toggle
                      checked={showVerticalGridlines}
                      onChange={(checked) => setShowVerticalGridlines(checked)}
                      size="sm"
                    />
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Show Zebra Striping on Tables
                  </label>
                  <p className="text-xs text-muted">
                    Alternate row background colors for readability
                  </p>
                </div>
                <Toggle
                  checked={showTableZebraStriping}
                  onChange={(checked) => setShowTableZebraStriping(checked)}
                  size="sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Compact Table Mode
                  </label>
                  <p className="text-xs text-muted">
                    Combine related columns to save horizontal space
                  </p>
                </div>
                <Toggle
                  checked={compactTableMode}
                  onChange={(checked) => setCompactTableMode(checked)}
                  size="sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Compact Order Details
                  </label>
                  <p className="text-xs text-muted">
                    Reduce padding and font sizes in the order drawer
                  </p>
                </div>
                <Toggle
                  checked={compactOrderDetails}
                  onChange={(checked) => setCompactOrderDetails(checked)}
                  size="sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Show Placeholder Text
                  </label>
                  <p className="text-xs text-muted">
                    Display the grey hint text inside empty input fields
                  </p>
                </div>
                <Toggle
                  checked={showInputPlaceholders}
                  onChange={(checked) => setShowInputPlaceholders(checked)}
                  size="sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Locked Fields as Plain Text
                  </label>
                  <p className="text-xs text-muted">
                    Show read-only and permission-locked values as plain text instead of a greyed-out input box
                  </p>
                </div>
                <Toggle
                  checked={plainTextLockedFields}
                  onChange={(checked) => setPlainTextLockedFields(checked)}
                  size="sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Style Locked Field Text
                  </label>
                  <p className="text-xs text-muted">
                    Pick the font color for locked values instead of the greyed-out default
                  </p>
                </div>
                <Toggle
                  checked={styleLockedFieldText}
                  onChange={(checked) => setStyleLockedFieldText(checked)}
                  size="sm"
                />
              </div>
              {styleLockedFieldText && (
                <div className="ml-4 pl-4 border-l-2 border-theme-border-secondary space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-label">Bold</label>
                    <Toggle
                      checked={lockedFieldTextBold}
                      onChange={(checked) => setLockedFieldTextBold(checked)}
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-label">Italic</label>
                    <Toggle
                      checked={lockedFieldTextItalic}
                      onChange={(checked) => setLockedFieldTextItalic(checked)}
                      size="sm"
                    />
                  </div>
                  {/* Live sample — reads the same CSS vars the real fields do,
                      so there's no second copy of the color math to drift. */}
                  <div className="rounded border border-theme-border-primary bg-theme-bg-tertiary px-2 py-1.5 text-sm">
                    <span
                      style={{
                        color: darkMode
                          ? 'rgb(var(--locked-field-text-dark))'
                          : 'rgb(var(--locked-field-text-light))',
                        fontWeight: lockedFieldTextBold ? 700 : undefined,
                        fontStyle: lockedFieldTextItalic ? 'italic' : undefined,
                      }}
                    >
                      02/27/2026 — locked field value
                    </span>
                  </div>
                  {darkMode ? (
                    <ZebraColorPicker
                      label="Dark Mode Locked Text Color"
                      color={lockedFieldTextColorDark}
                      onChange={setLockedFieldTextColorDark}
                      isDark={true}
                      baselineLightness={LOCKED_TEXT_BASELINE_LIGHTNESS}
                      saturationScale={1}
                      resetColor={DEFAULT_LOCKED_TEXT_COLOR_DARK}
                      resetLabel="Reset to default"
                    />
                  ) : (
                    <ZebraColorPicker
                      label="Light Mode Locked Text Color"
                      color={lockedFieldTextColorLight}
                      onChange={setLockedFieldTextColorLight}
                      isDark={false}
                      baselineLightness={LOCKED_TEXT_BASELINE_LIGHTNESS}
                      saturationScale={1}
                      resetColor={DEFAULT_LOCKED_TEXT_COLOR_LIGHT}
                      resetLabel="Reset to default"
                    />
                  )}
                </div>
              )}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-heading">
                      Locked Field Box Opacity
                    </label>
                    <p className="text-xs text-muted">
                      Fade the box around locked values — the value itself stays full strength
                    </p>
                  </div>
                  <span className="text-xs text-muted flex-shrink-0 ml-3">{lockedFieldChromeOpacity}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={lockedFieldChromeOpacity}
                  onChange={(e) => setLockedFieldChromeOpacity(parseInt(e.target.value, 10))}
                  aria-label="Locked Field Box Opacity"
                  className="mt-2 w-full h-3 rounded-lg appearance-none cursor-pointer bg-theme-bg-tertiary"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Tint Editable Field Boxes
                  </label>
                  <p className="text-xs text-muted">
                    Give editable input boxes a background color so they stand out from the page
                  </p>
                </div>
                <Toggle
                  checked={tintEditableFields}
                  onChange={(checked) => setTintEditableFields(checked)}
                  size="sm"
                />
              </div>
              {tintEditableFields && (
                <div className="ml-4 pl-4 border-l-2 border-theme-border-secondary space-y-3">
                  <FieldTintPreview label="Editable field" varName="--editable-field-tint-light" darkVarName="--editable-field-tint-dark" darkMode={darkMode} />
                  {darkMode ? (
                    <ZebraColorPicker
                      label="Dark Mode Editable Field Tint"
                      color={editableFieldTintDark}
                      onChange={setEditableFieldTintDark}
                      isDark={true}
                      baselineLightness={FIELD_TINT_BASELINE_DARK}
                      saturationScale={FIELD_TINT_SATURATION_SCALE}
                      resetColor={DEFAULT_FIELD_TINT_DARK}
                      resetLabel="Reset to default"
                    />
                  ) : (
                    <ZebraColorPicker
                      label="Light Mode Editable Field Tint"
                      color={editableFieldTintLight}
                      onChange={setEditableFieldTintLight}
                      isDark={false}
                      baselineLightness={FIELD_TINT_BASELINE_LIGHT}
                      saturationScale={FIELD_TINT_SATURATION_SCALE}
                      resetColor={DEFAULT_FIELD_TINT_LIGHT}
                      resetLabel="Reset to default"
                    />
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Tint Locked Field Boxes
                  </label>
                  <p className="text-xs text-muted">
                    Give locked input boxes their own background color, so locked and editable fields differ at a glance
                  </p>
                </div>
                <Toggle
                  checked={tintLockedFields}
                  onChange={(checked) => setTintLockedFields(checked)}
                  size="sm"
                />
              </div>
              {tintLockedFields && (
                <div className="ml-4 pl-4 border-l-2 border-theme-border-secondary space-y-3">
                  <FieldTintPreview label="Locked field" varName="--locked-field-tint-light" darkVarName="--locked-field-tint-dark" darkMode={darkMode} />
                  {darkMode ? (
                    <ZebraColorPicker
                      label="Dark Mode Locked Field Tint"
                      color={lockedFieldTintDark}
                      onChange={setLockedFieldTintDark}
                      isDark={true}
                      baselineLightness={FIELD_TINT_BASELINE_DARK}
                      saturationScale={FIELD_TINT_SATURATION_SCALE}
                      resetColor={DEFAULT_FIELD_TINT_DARK}
                      resetLabel="Reset to default"
                    />
                  ) : (
                    <ZebraColorPicker
                      label="Light Mode Locked Field Tint"
                      color={lockedFieldTintLight}
                      onChange={setLockedFieldTintLight}
                      isDark={false}
                      baselineLightness={FIELD_TINT_BASELINE_LIGHT}
                      saturationScale={FIELD_TINT_SATURATION_SCALE}
                      resetColor={DEFAULT_FIELD_TINT_LIGHT}
                      resetLabel="Reset to default"
                    />
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Full-Slide Navigation
                  </label>
                  <p className="text-xs text-muted">
                    When the sidebar is collapsed, hovering it slides the whole menu out instead of showing icon pop-outs
                  </p>
                </div>
                <Toggle
                  checked={navFullSlide}
                  onChange={(checked) => setNavFullSlide(checked)}
                  size="sm"
                />
              </div>

              <TextCasePreference />
              <FontPreferencesSection />

              <PrintPreferencesSection />

              <div className="flex items-center justify-between py-2 border-b border-theme-border-primary">
                <div>
                  <label className="text-sm font-medium text-heading">
                    Show Column Widths
                  </label>
                  <p className="text-xs text-muted">
                    Display pixel width on each table column header
                  </p>
                </div>
                <Toggle
                  checked={showColumnWidths}
                  onChange={(checked) => setShowColumnWidths(checked)}
                  size="sm"
                />
              </div>

              {/* Stripe color picker for the currently active mode */}
              {showTableZebraStriping && (
                <div className="mt-4 pt-4 border-t border-theme-border-primary space-y-6">
                  {darkMode ? (
                    <ZebraColorPicker
                      label="Dark Mode Stripe Color"
                      color={zebraColorDark}
                      onChange={setZebraColorDark}
                      isDark={true}
                      baselineLightness={18}
                      saturationScale={0.6}
                      showFontSlider
                    />
                  ) : (
                    <ZebraColorPicker
                      label="Light Mode Stripe Color"
                      color={zebraColorLight}
                      onChange={setZebraColorLight}
                      isDark={false}
                      baselineLightness={94}
                      saturationScale={0.6}
                      showFontSlider
                    />
                  )}
                </div>
              )}

              {/* Gridline color picker for the currently active mode */}
              {showTableGridlines && (
                <div className="mt-4 pt-4 border-t border-theme-border-primary space-y-6">
                  {darkMode ? (
                    <ZebraColorPicker
                      label="Dark Mode Gridline Color"
                      color={gridlineColorDark}
                      onChange={setGridlineColorDark}
                      isDark={true}
                      baselineLightness={34}
                      saturationScale={1}
                    />
                  ) : (
                    <ZebraColorPicker
                      label="Light Mode Gridline Color"
                      color={gridlineColorLight}
                      onChange={setGridlineColorLight}
                      isDark={false}
                      baselineLightness={90}
                      saturationScale={1}
                    />
                  )}
                </div>
              )}

              {/* Group row color picker */}
              <div className="mt-4 pt-4 border-t border-theme-border-primary space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-heading">Group Row Color</h4>
                  <p className="text-xs text-muted mt-0.5">Base color for grouped header rows. Child levels are calculated automatically.</p>
                </div>
                {/* Depth preview swatches */}
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map(depth => (
                    <div
                      key={depth}
                      className={`group-row-depth-${depth} flex-1 rounded text-center py-1 text-xs font-medium`}
                    >
                      Level {depth + 1}
                    </div>
                  ))}
                </div>
                {darkMode ? (
                  <ZebraColorPicker
                    label="Dark Mode Group Row Color"
                    color={groupRowColorDark}
                    onChange={setGroupRowColorDark}
                    isDark={true}
                    baselineLightness={28}
                    saturationScale={1}
                  />
                ) : (
                  <ZebraColorPicker
                    label="Light Mode Group Row Color"
                    color={groupRowColorLight}
                    onChange={setGroupRowColorLight}
                    isDark={false}
                    baselineLightness={36}
                    saturationScale={1}
                  />
                )}
                <button
                  onClick={() => {
                    if (darkMode) setGroupRowColorDark(DEFAULT_GROUP_ROW_COLOR);
                    else setGroupRowColorLight(DEFAULT_GROUP_ROW_COLOR);
                  }}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Reset to default
                </button>
              </div>
            </div>
          </details>

          {/* Account Settings */}
          <details className="group border border-theme-border-primary rounded-lg">
            <summary className="flex items-center justify-between cursor-pointer p-4 hover:bg-theme-bg-tertiary rounded-lg">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                <span className="font-medium text-heading">Account Settings</span>
              </div>
              <ChevronDownIcon className="w-5 h-5 text-muted transition-transform group-open:rotate-180" />
            </summary>
            <div className="p-4 border-t border-theme-border-primary">
              <p className="text-sm text-muted">
                Account settings coming soon...
              </p>
            </div>
          </details>

          {/* Application Preferences */}
          <details className="group border border-theme-border-primary rounded-lg">
            <summary className="flex items-center justify-between cursor-pointer p-4 hover:bg-theme-bg-tertiary rounded-lg">
              <div className="flex items-center gap-3">
                <Cog6ToothIcon className="w-5 h-5 text-muted" />
                <span className="font-medium text-heading">Application Preferences</span>
              </div>
              <ChevronDownIcon className="w-5 h-5 text-muted transition-transform group-open:rotate-180" />
            </summary>
            <div className="p-4 border-t border-theme-border-primary space-y-4">
              {/* Drivers Category */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-label">Drivers</h4>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Show Samsara Route ETAs
                  </label>
                  <Toggle checked={showSamsaraRouteEtas} onChange={setShowSamsaraRouteEtas}
                    ariaLabel="Show Samsara Route ETAs" size="sm" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Show Extended Working Status
                  </label>
                  <Toggle
                    checked={showExtendedWorkingStatus}
                    onChange={setShowExtendedWorkingStatus}
                    size="sm"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Planning View - Show All Moves
                  </label>
                  <Toggle
                    checked={planningShowAllMoves}
                    onChange={setPlanningShowAllMoves}
                    size="sm"
                  />
                </div>
              </div>
              {/* Dispatching Category */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-label">Dispatching</h4>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Default Send to gwiMove
                  </label>
                  <Toggle
                    checked={defaultSendToGwiMove}
                    onChange={setDefaultSendToGwiMove}
                    size="sm"
                  />
                </div>
              </div>
              {/* Pro Details Category */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-label">Pro Details</h4>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Auto Close On Save
                  </label>
                  <Toggle
                    checked={autoCloseProOnSave}
                    onChange={setAutoCloseProOnSave}
                    size="sm"
                  />
                </div>
              </div>
              {/* Follow Ups Category */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-label">Follow Ups</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                      Compact Follow Up Handle
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      On = the tab shrinks to just the icon and the count
                    </p>
                  </div>
                  <Toggle
                    checked={compactFollowUpHandle}
                    onChange={setCompactFollowUpHandle}
                    size="sm"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                      Hide Follow Up Handle When Empty
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      On = the tab disappears until you bookmark something
                    </p>
                  </div>
                  <Toggle
                    checked={hideFollowUpHandleWhenEmpty}
                    onChange={setHideFollowUpHandleWhenEmpty}
                    size="sm"
                  />
                </div>
              </div>
              {/* Downloads Category */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-label">Downloads</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                      Show Save As
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Off = files save straight to your Downloads folder
                    </p>
                  </div>
                  <Toggle
                    checked={showSaveAsDialog}
                    onChange={setShowSaveAsDialog}
                    size="sm"
                  />
                </div>
              </div>
            </div>
          </details>

          {/* Debug Settings */}
          <details className="group border border-theme-border-primary rounded-lg">
            <summary className="flex items-center justify-between cursor-pointer p-4 hover:bg-theme-bg-tertiary rounded-lg">
              <div className="flex items-center gap-3">
                <Cog6ToothIcon className="w-5 h-5 text-muted" />
                <span className="font-medium text-heading">Debug Settings</span>
              </div>
              <ChevronDownIcon className="w-5 h-5 text-muted transition-transform group-open:rotate-180" />
            </summary>
            <div className="p-4 border-t border-theme-border-primary space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Column Widths
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Show column widths on table headers
                  </p>
                </div>
                <Toggle
                  checked={showColumnWidths}
                  onChange={(checked) => setShowColumnWidths(checked)}
                  size="sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Grid Highlight
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Show colored borders and overlays on grid areas
                  </p>
                </div>
                <Toggle
                  checked={showGridHighlight}
                  onChange={(checked) => setShowGridHighlight(checked)}
                  size="sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Grid Labels
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Show grid area name labels in top-left corner
                  </p>
                </div>
                <Toggle
                  checked={showGridLabels}
                  onChange={(checked) => setShowGridLabels(checked)}
                  size="sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Grid Borders
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Show colored borders around grid areas
                  </p>
                </div>
                <Toggle
                  checked={showGridBorders}
                  onChange={(checked) => setShowGridBorders(checked)}
                  size="sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Field Names
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Show field identifiers with dotted borders (for help docs)
                  </p>
                </div>
                <Toggle
                  checked={showFieldNames}
                  onChange={(checked) => setShowFieldNames(checked)}
                  size="sm"
                />
              </div>
              {/* Production Readonly Mode - only show for authorized users */}
              {canEnableMode && (
                <div className="flex items-center justify-between pt-4 border-t border-theme-border-primary">
                  <div>
                    <label className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                      Production Readonly Mode
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Block ALL database writes. Shows what would be written via toast notifications.
                    </p>
                  </div>
                  <Toggle
                    checked={productionReadonlyMode}
                    onChange={(checked) => setProductionReadonlyMode(checked)}
                    size="sm"
                  />
                </div>
              )}
            </div>
          </details>
        </div>
      </Drawer>

      {/* Feature Request Modal */}
      <DraggableModal
        isOpen={featureRequestDrawerOpen}
        onClose={() => setFeatureRequestDrawerOpen(false)}
        title="Request a Feature"
        width={640}
        forceHidden={featureRequestDrawerTemporaryHidden}
        contentClassName="flex-1 overflow-y-auto"
        onPopOut={() => {
          const from = encodeURIComponent(window.location.href);
          openPopoutWindow(`/popout/feature-request?from=${from}`, 'fastcms-feature-request');
          setFeatureRequestDrawerOpen(false);
        }}
      >
        <FeatureRequestForm
          isOpen={featureRequestDrawerOpen}
          onCancel={() => setFeatureRequestDrawerOpen(false)}
          onTemporaryClose={() => setFeatureRequestDrawerTemporaryHidden(true)}
          onTemporaryReopen={() => setFeatureRequestDrawerTemporaryHidden(false)}
          onShowScreenshotModal={(screenshot, updateCallback) => {
            setScreenshotModalImage(screenshot);
            setScreenshotUpdateCallback(() => updateCallback);
            setScreenshotSource('feature');
            setScreenshotModalOpen(true);
            setFeatureRequestDrawerTemporaryHidden(true);
          }}
        />
      </DraggableModal>

      {/* Helpdesk (computer support) Ticket Modal */}
      <DraggableModal
        isOpen={helpdeskTicketDrawerOpen}
        onClose={() => setHelpdeskTicketDrawerOpen(false)}
        title="Helpdesk Ticket"
        width={640}
        forceHidden={helpdeskTicketDrawerTemporaryHidden}
        contentClassName="flex-1 overflow-y-auto"
        onPopOut={() => {
          const from = encodeURIComponent(window.location.href);
          openPopoutWindow(`/popout/helpdesk-ticket?from=${from}`, 'fastcms-helpdesk-ticket');
          setHelpdeskTicketDrawerOpen(false);
        }}
      >
        <HelpdeskTicketForm
          isOpen={helpdeskTicketDrawerOpen}
          onCancel={() => setHelpdeskTicketDrawerOpen(false)}
          onTemporaryClose={() => setHelpdeskTicketDrawerTemporaryHidden(true)}
          onTemporaryReopen={() => setHelpdeskTicketDrawerTemporaryHidden(false)}
          onShowScreenshotModal={(screenshot, updateCallback) => {
            setScreenshotModalImage(screenshot);
            setScreenshotUpdateCallback(() => updateCallback);
            setScreenshotSource('helpdesk');
            setScreenshotModalOpen(true);
            setHelpdeskTicketDrawerTemporaryHidden(true);
          }}
        />
      </DraggableModal>

      {/* Smart Preplan help shares the header information entry point. */}
      {location.pathname === '/dispatching/smart-preplanning-v2' ? (
        <AskSmartPreplanDrawer isOpen={isLegendOpen} onClose={closeLegend} />
      ) : (
        <IconLegendModal isOpen={isLegendOpen} onClose={closeLegend} sections={legendSections} title={legendTitle} />
      )}

      {/* Screenshot Editor Modal */}
      <ScreenshotEditor
        isOpen={screenshotModalOpen && !!screenshotModalImage}
        imageData={screenshotModalImage || ''}
        onClose={() => {
          setScreenshotModalOpen(false);
          setScreenshotModalImage(null);
          setScreenshotUpdateCallback(null);
          restoreTicketModalAfterScreenshot();
        }}
        onApply={(editedImageData) => {
          if (screenshotUpdateCallback) {
            screenshotUpdateCallback(editedImageData);
          }
          setScreenshotModalOpen(false);
          setScreenshotModalImage(null);
          setScreenshotUpdateCallback(null);
          restoreTicketModalAfterScreenshot();
        }}
      />
    </>
  );
}

export default CollapsibleDashboard;
